import { id, InstaQLEntity, lookup } from "@instantdb/react";
import { parsePhoneNumber } from "libphonenumber-js";
import { coerce } from "@uri/gamla";
import { apiClient, httpCommunication } from "@uri/typed-api";
import { ErrorBoundary, LocationProvider, Route, Router } from "preact-iso";
import hydrate from "preact-iso/hydrate";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { toast, Toaster } from "react-hot-toast";
import {
  lineServerApiDefinition,
  telegramApiDefinition,
  userApiDefinition,
} from "../../client/src/apiTyping.ts";
import { apiBaseUrl } from "../../client/src/main.ts";
import {
  type DashboardTab,
  monthlyPaymentPerLine,
  pbxmeNumberPriceUsd,
  supportEmail,
} from "../../constants.ts";
import schema, { whatsappAccountStatus } from "../../instant.schema.ts";
import { ApiDocs } from "./apiDocs.tsx";
import { auth, queryOnce, transact, tx, useAuth, useQuery } from "./db.ts";
import {
  downtimeBinsLast24h,
  downtimeMsLast24h,
  formatDowntime,
  uptimePercentageLast24h,
} from "./downtime.ts";
import {
  LegalPage,
  PrivacyPage,
  RefundPolicyPage,
  TermsPage,
} from "./legal.tsx";
import { Login } from "./login.tsx";
import {
  alertDialog,
  Button,
  confirmDialog,
  DialogHost,
  ErrorMessage,
  LoadingIndication,
  Modal,
  promptDialog,
  QrWithTimer,
  SecretToken,
  selectDialog,
} from "./uiComponents.tsx";
import { PhoneNumbersView } from "./phoneNumbers.tsx";
import { UserContext, useUser } from "./userContext.tsx";

const wrapperStyle = {
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  flexGrow: 1,
  width: "100%",
  height: "100vh", // Ensure wrapper fills viewport
  minHeight: 0, // Prevent flex children from growing unexpectedly
};

// Action row for account controls; wraps on narrow screens
const accountActionRowStyle = {
  display: "flex",
  gap: 8,
  flexDirection: "row",
  flexWrap: "wrap",
  marginTop: 10,
  paddingTop: 10,
  borderTop: "1px solid var(--border)",
} as const;

const paymentOptionsStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 12,
  marginTop: 12,
} as const;

const paymentOptionStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 8,
  padding: 16,
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  background: "var(--surface)",
  minHeight: 170,
  textAlign: "left",
} as const;

const paymentOptionTitleStyle = {
  fontSize: 17,
  fontWeight: 800,
  lineHeight: 1.2,
} as const;

const paymentOptionBodyStyle = {
  fontSize: 13,
  lineHeight: 1.45,
  opacity: 0.78,
  flexGrow: 1,
} as const;

// Connection help section styles and content
const connectHelpStyle = {
  marginTop: 12,
  padding: 12,
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  background: "var(--surface-alt)",
  display: "flex",
  flexDirection: "column",
  gap: 8,
} as const;

const adminChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "var(--surface-alt)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  padding: "2px 8px",
} as const;

export const ConnectHelp = () => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div style={connectHelpStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          ❓ Connection Help
        </div>
        <Button
          className="secondary"
          style={{ fontSize: 12, padding: "4px 10px" }}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? "Hide" : "Show"}
        </Button>
      </div>
      {isOpen && (
        <ul
          style={{
            margin: 0,
            paddingLeft: 18,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <li>
            4️⃣ WhatsApp supports up to four linked devices. In WhatsApp on your
            phone: Settings → Linked Devices. If you're at the limit, disconnect
            one and try again.
          </li>
          <li>
            🔳 QR codes expire quickly. If the QR disappears or fails, refresh
            and press <strong>Send QR Code</strong> again.
          </li>
          <li>
            📨 If you can't scan a QR, or you're connecting on the same device,
            use <strong>Send Login Code</strong>.
          </li>
          <li>
            🔄 It may take a few attempts. If a try fails, trigger{" "}
            <strong>Send QR Code</strong> or <strong>Send Login Code</strong>
            {" "}
            again.
          </li>
          <li>
            📶 Make sure WhatsApp on your phone is online, the app is up to
            date, and battery saver/background restrictions aren’t blocking it.
          </li>
          <li>
            🧹 If you get stuck, use <strong>Reset</strong>{" "}
            to clear credentials, then try connecting again.
          </li>
          <li
            style={{
              marginTop: 8,
              borderTop: "1px solid var(--border)",
              paddingTop: 8,
            }}
          >
            📧 Need help with billing or account limits? Contact{" "}
            <a
              href={`mailto:${supportEmail}`}
              style={{ color: "var(--primary)" }}
            >
              {supportEmail}
            </a>
          </li>
        </ul>
      )}
    </div>
  );
};

const Header = () => {
  const { user } = useAuth();

  return (
    <div
      style={{
        padding: "10px 20px",
        backgroundColor: "#282c34",
        color: "white",
        display: "flex",
        alignItems: "center",
        gap: "24px",
        flexWrap: "wrap",
      }}
    >
      <a href="/" style={{ color: "white", textDecoration: "none" }}>
        <h1 style={{ margin: 0 }}>Supergreen</h1>
      </a>
      <nav
        style={{
          display: "flex",
          gap: "16px",
          fontSize: "14px",
          flex: 1,
        }}
      >
        <a
          href="/app"
          style={{ color: "white", textDecoration: "none", opacity: 0.9 }}
          onMouseOver={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.opacity = "0.9";
          }}
        >
          Dashboard
        </a>
        <a
          href="/docs"
          style={{ color: "white", textDecoration: "none", opacity: 0.9 }}
          onMouseOver={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.opacity = "0.9";
          }}
        >
          API Docs
        </a>
        <a
          href="/pricing"
          style={{ color: "white", textDecoration: "none", opacity: 0.9 }}
          onMouseOver={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.opacity = "0.9";
          }}
        >
          Pricing
        </a>
        {user?.email === "uri.valevski@gmail.com" && (
          <a
            href="/admin"
            style={{ color: "white", textDecoration: "none", opacity: 0.9 }}
            onMouseOver={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.opacity = "0.9";
            }}
          >
            Admin Panel
          </a>
        )}
      </nav>
      {user?.email && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontSize: 14,
            marginLeft: "auto",
          }}
        >
          <span style={{ opacity: 0.9 }}>{user.email}</span>
          <Button
            className="secondary"
            style={{
              fontSize: 12,
              padding: "5px 10px",
              borderColor: "rgba(255,255,255,0.35)",
              color: "white",
            }}
            onClick={() => {
              toast
                .promise(auth.signOut(), {
                  loading: "Signing out...",
                  success: "Signed out",
                  error: "Failed to sign out",
                })
                .then(() => {
                  globalThis.location.href = "/";
                });
            }}
          >
            Sign out
          </Button>
        </div>
      )}
    </div>
  );
};

const footerLinkStyle = {
  color: "var(--text-muted)",
  textDecoration: "none",
  fontSize: "13px",
};

const Footer = () => (
  <footer
    style={{
      padding: "20px",
      textAlign: "center",
      display: "flex",
      justifyContent: "center",
      gap: "16px",
      flexWrap: "wrap",
      borderTop: "1px solid var(--border)",
      marginTop: "auto",
    }}
  >
    <a href="/terms" style={footerLinkStyle}>Terms</a>
    <a href="/privacy" style={footerLinkStyle}>Privacy</a>
    <a href="/refund-policy" style={footerLinkStyle}>Refunds</a>
  </footer>
);

const client = apiClient(
  httpCommunication(apiBaseUrl),
  lineServerApiDefinition,
);
const telegramClient = apiClient(
  httpCommunication(apiBaseUrl),
  telegramApiDefinition,
);
const userClient = apiClient(httpCommunication(apiBaseUrl), userApiDefinition);

const normalizeWaId = (input: string) => {
  const s = input.trim();
  if (!s) return s;
  // If user already provided a full chat id (contains @), keep as-is
  if (s.includes("@")) return s;
  const digits = s.replace(/\D/g, "");
  return `${digits}@c.us`;
};

const getToken = async (phoneNumber: string) => {
  const { data } = await queryOnce({
    whatsappAccounts: { $: { where: { phoneNumber } } },
  });
  return data?.whatsappAccounts?.[0]?.secretToken || "";
};

const getTelegramToken = async (phoneNumber: string) => {
  const { data } = await queryOnce({
    telegramAccounts: { $: { where: { phoneNumber } } },
  });
  return data?.telegramAccounts?.[0]?.secretToken || "";
};

const addAdmin = async (
  accountId: string,
  existingEmails: ReadonlyArray<string>,
) => {
  const raw = await promptDialog("Enter admin email:", {
    title: "Add admin",
    placeholder: "name@example.com",
    inputMode: "email",
  });
  if (!raw) return;
  const email = raw.trim();
  if (!email) return;
  if (existingEmails.includes(email)) {
    toast.error("Admin already added");
    return;
  }
  const promise = transact(
    tx.whatsappAccounts[accountId].link({ admins: lookup("email", email) }),
  );
  await toast.promise(promise, {
    loading: "Adding admin...",
    success: "Admin added",
    // deno-lint-ignore no-explicit-any
    error: (e: any) => {
      if (
        e.body?.message?.includes("does not exist") ||
        e.message?.includes("does not exist")
      ) {
        return `User ${email} must log in at least once first.`;
      }
      return e?.message || "Failed to add admin";
    },
  });
};

const removeAdmin = async (
  accountId: string,
  adminId: string,
  adminEmail?: string,
) => {
  const ok = await confirmDialog(
    `Remove ${adminEmail ?? "admin"} from this WhatsApp account?`,
    { title: "Remove admin", okLabel: "Remove", danger: true },
  );
  if (!ok) return;
  const promise = transact(
    tx.whatsappAccounts[accountId].unlink({ admins: adminId }),
  );
  await toast.promise(promise, {
    loading: "Removing admin...",
    success: "Admin removed",
    error: (e) => e?.message || "Failed to remove admin",
  });
};

const sendTest = async (fromNumber: string, userWhatsapp?: string) => {
  const raw = await promptDialog(
    "Enter destination phone (digits or full chat id):",
    {
      title: "Send test message",
      defaultValue: userWhatsapp || "",
      placeholder: "1...",
      inputMode: "tel",
    },
  );
  if (!raw) return;
  if (!raw.includes("@") && raw.replace(/\D/g, "").startsWith("0")) {
    toast.error("Please use international format (e.g. 972...)");
    return;
  }
  const toNumber = normalizeWaId(raw);
  if (!toNumber) {
    toast.error("No phone number provided");
    return;
  }
  const token = await getToken(fromNumber);
  const promise = (async () => {
    // Send typing indication first
    const typingResult = await client({
      endpoint: "sendTypingIndication",
      payload: {
        fromNumber,
        chatId: toNumber,
        durationMs: 2000,
        token,
      },
    });
    if ("error" in typingResult) throw new Error(String(typingResult.error));

    // Wait a brief moment to simulate typing
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Send the actual message
    const res = await client({
      endpoint: "sendMessage",
      payload: {
        fromNumber,
        toNumber,
        message: "Hello World!",
        linkPreview: false,
        token,
      },
    });

    if ("error" in res) throw new Error(String(res.error));
    return res;
  })();

  await toast.promise(promise, {
    loading: "Typing and sending...",
    success: () => "Message sent",
    error: (e) =>
      e?.message ? `Failed: ${e.message}` : "Failed to send message",
  });
};

const sendTelegramTest = async (phoneNumber: string) => {
  const raw = await promptDialog(
    "Enter a writable destination Telegram chat ID or @username:",
    { title: "Send Telegram test", placeholder: "@username or chat id" },
  );
  if (!raw) return;
  const chatId = raw.trim();
  const token = await getTelegramToken(phoneNumber);
  const promise = telegramClient({
    endpoint: "sendTelegramMessage",
    payload: { phoneNumber, chatId, text: "Hello World!", token },
  }).then((res) => {
    if ("error" in res) throw new Error(String(res.error));
    return res;
  });
  await toast.promise(promise, {
    loading: "Sending...",
    success: () => "Message sent",
    error: (e) =>
      e?.message ? `Failed: ${e.message}` : "Failed to send message",
  });
};

const listTelegramGroups = async (phoneNumber: string) => {
  const token = await getTelegramToken(phoneNumber);
  const promise = telegramClient({
    endpoint: "getTelegramGroups",
    payload: { phoneNumber, token },
  }).then((res) => {
    if ("error" in res) throw new Error(String(res.error));
    return res;
  });
  const result = await toast.promise(promise, {
    loading: "Loading groups...",
    success: "Groups loaded",
    error: (e) => e?.message || "Failed to list groups",
  });
  await alertDialog(
    result.groups.length === 0
      ? "No groups found"
      : result.groups.map((g) => `${g.name}: ${g.id}`).join("\n"),
    { title: "Telegram groups" },
  );
};

const reconnectingNumbers = new Set<string>();
const reconnectingListeners = new Set<() => void>();

const notifyReconnectingChanged = () =>
  reconnectingListeners.forEach((l) => l());

const useIsReconnecting = (number: string) => {
  const [isReconnecting, setIsReconnecting] = useState(
    reconnectingNumbers.has(number),
  );
  useEffect(() => {
    const listener = () => setIsReconnecting(reconnectingNumbers.has(number));
    reconnectingListeners.add(listener);
    return () => {
      reconnectingListeners.delete(listener);
    };
  }, [number]);
  return isReconnecting;
};

const isAssigningServerError = (message: string) =>
  message.startsWith("HTTP 202") && message.includes("assigning server");

const reconnectErrorMessage = (e: { message?: string }) => {
  const message = e?.message || "";
  if (isAssigningServerError(message)) {
    return "WhatsApp worker is still starting. Please try reconnect again in 2 minutes. If it still fails, contact support.";
  }
  return message ? `Failed: ${message}` : "Failed to trigger reconnect";
};

const reconnect = async (number: string) => {
  if (reconnectingNumbers.has(number)) return;
  reconnectingNumbers.add(number);
  notifyReconnectingChanged();
  try {
    const token = await getToken(number);
    const promise = client({
      endpoint: "reconnect",
      payload: { number, token },
    });
    await toast.promise(promise, {
      loading: "Reconnecting...",
      success: () => "Reconnect triggered",
      error: reconnectErrorMessage,
    });
  } finally {
    reconnectingNumbers.delete(number);
    notifyReconnectingChanged();
  }
};

const setLoginMethod = async (
  number: string,
  preferCodeLogin: boolean,
) => {
  const token = await getToken(number);
  await toast.promise(
    client({
      endpoint: "setLoginMethod",
      payload: { phoneNumber: number, preferCodeLogin, token },
    }),
    {
      loading: preferCodeLogin
        ? "Switching to code login..."
        : "Switching to QR login...",
      success: () =>
        preferCodeLogin ? "Switched to code login" : "Switched to QR login",
      error: (e) =>
        e?.message ? `Failed: ${e.message}` : "Failed to switch login method",
    },
  );
};

const _setWarmupParticipation = async (
  refreshToken: string,
  phoneNumber: string,
  enabled: boolean,
) => {
  await toast.promise(
    userClient({
      endpoint: "setWarmupParticipation",
      payload: {
        refreshToken,
        phoneNumber,
        enabled,
      },
    }).then((result) => {
      if ("error" in result) throw new Error(result.error);
      return result;
    }),
    {
      loading: enabled
        ? "Joining warmup network..."
        : "Leaving warmup network...",
      success: () => enabled ? "Warmup enabled" : "Warmup disabled",
      error: (e) =>
        e?.message ? `Failed: ${e.message}` : "Failed to update warmup",
    },
  );
};

const changeAccountName = async (phoneNumber: string) => {
  const name = await promptDialog("Enter new WhatsApp account name:", {
    title: "Change account name",
  });
  if (!name || !name.trim()) return;
  await toast.promise(
    client({
      endpoint: "changeWhatsappAccountName",
      payload: {
        phoneNumber,
        name: name.trim(),
        token: await getToken(phoneNumber),
      },
    }),
    {
      loading: "Changing account name...",
      success: () => "Account name changed",
      error: (e) =>
        e?.message ? `Failed: ${e.message}` : "Failed to change account name",
    },
  );
};

const changeAccountBio = async (phoneNumber: string) => {
  const bio = await promptDialog("Enter new WhatsApp bio:", {
    title: "Change bio",
    helperText:
      "Note: If your bio isn't visible, make sure your 'About' privacy setting is set to 'Everyone' using the '🔒 Privacy Settings' button below.",
  });
  if (bio === null) return;
  await toast.promise(
    client({
      endpoint: "changeWhatsappAccountBio",
      payload: {
        phoneNumber,
        bio: bio.trim(),
        token: await getToken(phoneNumber),
      },
    }),
    {
      loading: "Changing bio...",
      success: () => "Bio changed",
      error: (e) =>
        e?.message ? `Failed: ${e.message}` : "Failed to change bio",
    },
  );
};

const changeAccountPrivacy = async (phoneNumber: string) => {
  const category = await selectDialog(
    [
      {
        value: "about",
        label: "💬 About / Bio Privacy",
        description: "Who can see your about text / bio",
      },
      {
        value: "profilePic",
        label: "🖼️ Profile Photo Privacy",
        description: "Who can see your profile picture",
      },
    ],
    {
      title: "Choose Privacy Category",
      message: "Which privacy setting do you want to change?",
    },
  );
  if (!category) return;

  const value = await selectDialog(
    [
      {
        value: "all",
        label: "👥 Everyone",
        description: "Visible to anyone who has your number",
      },
      {
        value: "contacts",
        label: "👥 My Contacts",
        description: "Visible only to your WhatsApp contacts",
      },
      {
        value: "none",
        label: "🔒 Nobody",
        description: "Hidden from everyone",
      },
    ],
    {
      title: "Choose Privacy Level",
      message: `Who should be able to see your ${
        category === "about" ? "about / bio" : "profile photo"
      }?`,
    },
  );
  if (!value) return;

  await toast.promise(
    client({
      endpoint: "changeWhatsappPrivacySettings",
      payload: {
        phoneNumber,
        category: category as "about" | "profilePic",
        value: value as "all" | "contacts" | "none",
        token: await getToken(phoneNumber),
      },
    }),
    {
      loading: "Updating privacy setting...",
      success: () => "Privacy setting updated successfully",
      error: (e) =>
        e?.message
          ? `Failed: ${e.message}`
          : "Failed to update privacy setting",
    },
  );
};

const changeAccountAvatar = (phoneNumber: string) => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.onchange = (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target?.result as string;
      if (!dataUrl) {
        toast.error("Failed to read image");
        return;
      }
      const token = await getToken(phoneNumber);
      const promise = client({
        endpoint: "changeWhatsappAccountAvatar",
        payload: { phoneNumber, imageBase64: dataUrl, token },
      });
      await toast.promise(promise, {
        loading: "Uploading avatar...",
        success: () =>
          "Avatar updated. If it doesn't show, make sure 'Profile Photo' privacy is set to 'Everyone' in '🔒 Privacy Settings'.",
        error: (e) =>
          e?.message ? `Failed: ${e.message}` : "Failed to update avatar",
      });
    };
    reader.readAsDataURL(file);
  };
  input.click();
};

const editAccountName = async (
  accountId: string,
  currentName: string,
) => {
  const raw = await promptDialog("Edit account name:", {
    title: "Edit account name",
    defaultValue: currentName,
  });
  if (raw === null) return;
  const newName = raw.trim();
  if (newName === currentName) return;
  const promise = transact(
    tx.whatsappAccounts[accountId].update({ name: newName }),
  );
  await toast.promise(promise, {
    loading: "Saving name...",
    success: "Account name updated",
    error: (e) => e?.message || "Failed to update account name",
  });
};

const editAccountNotes = async (
  accountId: string,
  currentNotes?: string,
) => {
  const raw = await promptDialog("Edit notes:", {
    title: "Edit notes",
    defaultValue: currentNotes ?? "",
  });
  if (raw === null) return;
  const trimmed = raw.trim();
  const notes = trimmed === "" ? null : trimmed;
  const promise = transact(
    tx.whatsappAccounts[accountId].update({ notes }),
  );
  await toast.promise(promise, {
    loading: "Saving notes...",
    success: () => (notes ? "Notes updated" : "Notes removed"),
    error: (e) => e?.message || "Failed to update notes",
  });
};

const mergeWebhooks = (
  webhook?: string,
  webhookUrls?: string[],
): string[] => [
  ...new Set([...(webhookUrls ?? []), ...(webhook ? [webhook] : [])]),
];

const handleAddWebhook =
  (phoneNumber: string, setUrls: (fn: (prev: string[]) => string[]) => void) =>
  async () => {
    const url = await promptDialog("Enter webhook URL:", {
      title: "Add webhook",
      placeholder: "https://example.com/webhook",
      inputMode: "url",
    });
    if (!url) return;
    const trimmed = url.trim();
    if (!trimmed) return;
    const token = await getToken(phoneNumber);
    const promise = client({
      endpoint: "addWebhook",
      payload: { phoneNumber, url: trimmed, token },
    });
    await toast.promise(promise, {
      loading: "Adding webhook...",
      success: () => "Webhook added",
      error: (e) => e?.message || "Failed to add webhook",
    });
    setUrls((prev) => [...new Set([...prev, trimmed])]);
  };

const handleRemoveWebhook = (
  phoneNumber: string,
  url: string,
  setUrls: (fn: (prev: string[]) => string[]) => void,
) =>
async () => {
  if (
    !await confirmDialog(`Remove webhook?\n${url}`, {
      title: "Remove webhook",
      okLabel: "Remove",
      danger: true,
    })
  ) return;
  const token = await getToken(phoneNumber);
  const promise = client({
    endpoint: "removeWebhook",
    payload: { phoneNumber, url, token },
  });
  await toast.promise(promise, {
    loading: "Removing webhook...",
    success: () => "Webhook removed",
    error: (e) => e?.message || "Failed to remove webhook",
  });
  setUrls((prev) => prev.filter((u) => u !== url));
};

const handleAddTelegramWebhook = (
  phoneNumber: string,
  setUrls: (fn: (prev: string[]) => string[]) => void,
) =>
async () => {
  const trimmed = (await promptDialog("Enter webhook URL:", {
    title: "Add webhook",
    placeholder: "https://example.com/webhook",
    inputMode: "url",
  }))?.trim();
  if (!trimmed) return;
  const token = await getTelegramToken(phoneNumber);
  const promise = telegramClient({
    endpoint: "addTelegramWebhook",
    payload: { phoneNumber, url: trimmed, token },
  });
  await toast.promise(promise, {
    loading: "Adding webhook...",
    success: () => "Webhook added",
    error: (e) => e?.message || "Failed to add webhook",
  });
  setUrls((prev) => [...new Set([...prev, trimmed])]);
};

const handleRemoveTelegramWebhook = (
  phoneNumber: string,
  url: string,
  setUrls: (fn: (prev: string[]) => string[]) => void,
) =>
async () => {
  if (
    !await confirmDialog(`Remove webhook?\n${url}`, {
      title: "Remove webhook",
      okLabel: "Remove",
      danger: true,
    })
  ) return;
  const token = await getTelegramToken(phoneNumber);
  const promise = telegramClient({
    endpoint: "removeTelegramWebhook",
    payload: { phoneNumber, url, token },
  });
  await toast.promise(promise, {
    loading: "Removing webhook...",
    success: () => "Webhook removed",
    error: (e) => e?.message || "Failed to remove webhook",
  });
  setUrls((prev) => prev.filter((u) => u !== url));
};

const webhookItemStyle = {
  fontSize: 12,
  opacity: 0.75,
  wordBreak: "break-all",
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const WebhookEditor = (
  { phoneNumber, webhook, webhookUrls }: {
    phoneNumber: string;
    webhook?: string;
    webhookUrls?: string[];
  },
) => {
  const [urls, setUrls] = useState(() => mergeWebhooks(webhook, webhookUrls));
  return (
    <div
      style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}
    >
      {urls.map((url) => (
        <div key={url} style={webhookItemStyle}>
          <span>{url}</span>
          <Button
            className="secondary"
            style={{ fontSize: 11, padding: "2px 6px" }}
            onClick={handleRemoveWebhook(phoneNumber, url, setUrls)}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        className="secondary"
        style={{ fontSize: 12, alignSelf: "flex-start" }}
        onClick={handleAddWebhook(phoneNumber, setUrls)}
      >
        + Add webhook
      </Button>
    </div>
  );
};

const TgWebhookEditor = (
  { phoneNumber, webhook, webhookUrls }: {
    phoneNumber: string;
    webhook?: string;
    webhookUrls?: string[];
  },
) => {
  const [urls, setUrls] = useState(() => mergeWebhooks(webhook, webhookUrls));
  return (
    <div
      style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}
    >
      {urls.map((url) => (
        <div key={url} style={webhookItemStyle}>
          <span>{url}</span>
          <Button
            className="secondary"
            style={{ fontSize: 11, padding: "2px 6px" }}
            onClick={handleRemoveTelegramWebhook(phoneNumber, url, setUrls)}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        className="secondary"
        style={{ fontSize: 12, alignSelf: "flex-start" }}
        onClick={handleAddTelegramWebhook(phoneNumber, setUrls)}
      >
        + Add webhook
      </Button>
    </div>
  );
};

const useCheckoutSuccess = () => {
  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    if (params.get("checkout") === "success") {
      toast.success("Payment received! Your capacity will update shortly.");
      const url = new URL(globalThis.location.href);
      url.searchParams.delete("checkout");
      globalThis.history.replaceState({}, "", url.toString());
    }
    if (params.get("billing") === "updated") {
      toast.success("Subscription updated. Your services will adjust shortly.");
      const url = new URL(globalThis.location.href);
      url.searchParams.delete("billing");
      globalThis.history.replaceState({}, "", url.toString());
    }
  }, []);
};

const startLineCheckout = async (refreshToken: string) => {
  const checkoutClient = apiClient(
    httpCommunication(apiBaseUrl),
    { createCheckoutUrl: userApiDefinition.createCheckoutUrl },
  );
  const promise = checkoutClient({
    endpoint: "createCheckoutUrl",
    payload: { refreshToken },
  }).then((res) => {
    if ("error" in res) throw new Error(String(res.error));
    if ("url" in res) {
      globalThis.location.href = res.url;
      return;
    }
    throw new Error("No URL returned");
  });
  await toast.promise(promise, {
    loading: "Redirecting to checkout...",
    success: "Redirecting...",
    error: (e) => e?.message || "Failed to create checkout session",
  });
};

const openBillingPortal = async (refreshToken: string) => {
  const portalClient = apiClient(
    httpCommunication(apiBaseUrl),
    { createBillingPortalUrl: userApiDefinition.createBillingPortalUrl },
  );
  const promise = portalClient({
    endpoint: "createBillingPortalUrl",
    payload: { refreshToken },
  }).then((res) => {
    if ("error" in res) throw new Error(String(res.error));
    if ("url" in res) {
      globalThis.location.href = res.url;
      return;
    }
    throw new Error("No URL returned");
  });
  await toast.promise(promise, {
    loading: "Opening billing portal...",
    success: "Redirecting...",
    error: (e) => e?.message || "Failed to open billing portal",
  });
};

const startAuthenticatedPayment = (
  refreshToken: string | undefined,
  action: (refreshToken: string) => Promise<void>,
) =>
async () => {
  if (!refreshToken) {
    toast.error("Not authenticated");
    return;
  }
  await action(refreshToken);
};

const useBillingData = () => {
  const { user: authUser } = useAuth();
  const { data: servicesData } = useQuery(
    authUser?.email
      ? {
        whatsappAccounts: {
          $: { where: { isEnabled: true } },
        },
        telegramAccounts: {
          $: { where: { isEnabled: true } },
        },
        phoneNumbers: {
          $: { where: { status: "active" } },
        },
        fbAccounts: {
          $: { where: { status: { $in: ["active", "loginRequired"] } } },
        },
      }
      : null,
  );
  const { dbUser } = useUser();
  useCheckoutSuccess();
  if (!dbUser) return null;
  const user = dbUser;

  const monthlyPayment = user.monthlyPayment || 0;
  const paidCapacityLegacy = Math.floor(monthlyPayment / monthlyPaymentPerLine);
  const paidCapacity = Math.max(paidCapacityLegacy, user.paidCapacity || 0);
  const createdAt = user.createdAt || Date.now();
  const trialEnd = new Date(createdAt + 30 * 24 * 60 * 60 * 1000);
  const isTrial = Date.now() < trialEnd.getTime();
  const trialCapacity = isTrial ? 1 : 0;
  const capacity = Math.max(paidCapacity, trialCapacity);
  const budget = capacity * monthlyPaymentPerLine;
  const paidBudget = paidCapacity * monthlyPaymentPerLine;

  const waCount = servicesData?.whatsappAccounts?.length || 0;
  const tgCount = servicesData?.telegramAccounts?.length || 0;
  const numbers = servicesData?.phoneNumbers || [];
  const fbCount = servicesData?.fbAccounts?.length || 0;
  const numberTotalPrice = numbers.reduce(
    (sum, n) => sum + (n.price || pbxmeNumberPriceUsd),
    0,
  );
  const spent = waCount * monthlyPaymentPerLine +
    tgCount * monthlyPaymentPerLine +
    numberTotalPrice + fbCount * monthlyPaymentPerLine;
  const remaining = budget - spent;
  const paidServiceSpend = numberTotalPrice +
    Math.max(0, waCount + tgCount + fbCount - trialCapacity) *
      monthlyPaymentPerLine;
  const paidRemaining = Math.min(remaining, paidBudget - paidServiceSpend);

  return {
    user,
    authUser,
    waCount,
    tgCount,
    numbers,
    fbCount,
    numberTotalPrice,
    spent,
    remaining,
    budget,
    capacity,
    trialCapacity,
    isTrial,
    trialEnd,
    paidRemaining,
  };
};

const BillingTab = () => {
  const data = useBillingData();
  const [manageOpen, setManageOpen] = useState(false);
  if (!data) return null;
  const {
    user: _user,
    authUser,
    waCount,
    tgCount,
    numbers,
    fbCount,
    numberTotalPrice,
    spent,
    remaining,
    budget,
    capacity,
    trialCapacity,
    isTrial,
    trialEnd,
    paidRemaining,
  } = data;

  return (
    <div
      style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <h2 style={{ margin: 0 }}>Billing</h2>
      <div
        style={{
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          padding: 16,
          background: "var(--surface-alt)",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600 }}>
          Plan Summary
        </div>
        <div style={{ fontSize: 14, opacity: 0.9 }}>
          <div>
            Available budget: <strong>${budget}/mo</strong> ({capacity}{" "}
            slot{capacity === 1 ? "" : "s"})
          </div>
          {trialCapacity > 0 && (
            <div style={{ marginTop: 8, fontSize: 13, opacity: 0.8 }}>
              Free trial includes {trialCapacity}{" "}
              service slot for WhatsApp, Telegram, or Facebook during your first
              month. Phone numbers require paid budget.
            </div>
          )}
          {spent > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ opacity: 0.85, fontWeight: 500 }}>Spent on:</div>
              {waCount > 0 && (
                <div style={{ paddingLeft: 12, marginTop: 2 }}>
                  - WhatsApp lines: {waCount} x ${monthlyPaymentPerLine}
                  = ${waCount * monthlyPaymentPerLine}
                </div>
              )}
              {tgCount > 0 && (
                <div style={{ paddingLeft: 12, marginTop: 2 }}>
                  - Telegram accounts: {tgCount} x ${monthlyPaymentPerLine}
                  = ${tgCount * monthlyPaymentPerLine}
                </div>
              )}
              {numbers.length > 0 && (
                <div style={{ paddingLeft: 12, marginTop: 2 }}>
                  - Phone numbers: {numbers.length} x ${pbxmeNumberPriceUsd}
                  = ${numberTotalPrice}
                </div>
              )}
              {fbCount > 0 && (
                <div style={{ paddingLeft: 12, marginTop: 2 }}>
                  - Facebook accounts: {fbCount} x ${monthlyPaymentPerLine}
                  = ${fbCount * monthlyPaymentPerLine}
                </div>
              )}
              <div style={{ marginTop: 4 }}>
                Total spent: <strong>${spent}</strong>
              </div>
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            Remaining: <strong>${remaining}</strong>
          </div>
          <div style={{ marginTop: 4, fontSize: 13, opacity: 0.8 }}>
            Paid number budget remaining: <strong>${paidRemaining}</strong>
          </div>
          {capacity === 0 && (
            <div style={{ fontSize: 13, opacity: 0.7, marginTop: 8 }}>
              {isTrial
                ? (
                  <span>
                    Trial expires on {trialEnd.toLocaleDateString()}
                  </span>
                )
                : <span>Free trial ended. Add budget to add services.</span>}
            </div>
          )}
        </div>
        <div style={paymentOptionsStyle}>
          <div style={paymentOptionStyle}>
            <div style={paymentOptionTitleStyle}>
              Add budget
            </div>
            <div style={paymentOptionBodyStyle}>
              Purchase additional slots to add WhatsApp, Telegram, Facebook, or
              SMS number capacity. Phone numbers always require paid budget.
            </div>
            <Button
              className="primary"
              style={{ fontSize: 15, padding: "10px 18px", width: "auto" }}
              onClick={startAuthenticatedPayment(
                authUser?.refresh_token,
                startLineCheckout,
              )}
            >
              + Add budget
            </Button>
            <div style={{ fontSize: 12, opacity: 0.65 }}>
              ${monthlyPaymentPerLine}/month per slot
            </div>
            <Button
              className="secondary"
              style={{
                fontSize: 14,
                padding: "8px 16px",
                width: "auto",
                marginTop: 8,
              }}
              onClick={() => setManageOpen(true)}
            >
              Manage subscription
            </Button>
          </div>
        </div>
        <div
          style={{
            marginTop: 8,
            paddingTop: 12,
            borderTop: "1px solid var(--border)",
            fontSize: 13,
            opacity: 0.8,
          }}
        >
          One paid capacity slot covers any service type. The free trial covers
          one WhatsApp, Telegram, or Facebook account. For billing questions,
          email{" "}
          <a
            href={`mailto:${supportEmail}`}
            style={{ color: "var(--primary)" }}
          >
            {supportEmail}
          </a>
        </div>
      </div>
      <ManageBillingModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        refreshToken={authUser?.refresh_token}
        waCount={waCount}
        tgCount={tgCount}
        fbCount={fbCount}
        numberCount={numbers.length}
      />
    </div>
  );
};

const UserProfile = () => {
  const { dbUser } = useUser();
  if (!dbUser) return null;
  const user = dbUser;

  return (
    <div
      style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <h2 style={{ margin: 0 }}>Your Profile</h2>
      <div
        style={{
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          padding: 12,
          background: "var(--surface-alt)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, opacity: 0.85 }}>
              Your Personal WhatsApp:
            </span>
            <span style={{ fontWeight: 600 }}>
              {user.whatsapp || "Not set"}
            </span>
            <Button
              className="secondary"
              style={{ fontSize: 12, padding: "4px 12px" }}
              onClick={async () => {
                const raw = await promptDialog(
                  "Enter your WhatsApp number (e.g., 15551234567):",
                  {
                    title: user.whatsapp
                      ? "Edit your WhatsApp number"
                      : "Add your WhatsApp number",
                    defaultValue: user.whatsapp || "",
                    placeholder: "15551234567",
                    inputMode: "tel",
                  },
                );
                if (raw === null) return;
                const trimmed = raw.replace(/\D/g, "");
                if (trimmed.startsWith("0")) {
                  toast.error("Please use international format (e.g. 1...)");
                  return;
                }
                const whatsapp = trimmed === "" ? null : trimmed;
                const promise = transact(
                  tx.users[user.id].update({ whatsapp }),
                );
                await toast.promise(promise, {
                  loading: "Saving...",
                  success: () =>
                    whatsapp
                      ? "WhatsApp number updated"
                      : "WhatsApp number removed",
                  error: (e) => e?.message || "Failed to save",
                });
              }}
            >
              {user.whatsapp ? "✏ Edit" : "+ Add"}
            </Button>
          </div>
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
            ⚠️ Use this for receiving test messages and communicate with
            support, this is NOT your bot's number.
          </div>
        </div>
      </div>
    </div>
  );
};

const yellowStatuses = new Set<string>([
  whatsappAccountStatus.waitingForSsh,
  whatsappAccountStatus.startingDocker,
  whatsappAccountStatus.provisioningServer,
  whatsappAccountStatus.serverProvisioned,
  whatsappAccountStatus.dockerStarted,
  "clientCreated",
  "streamMode:QR",
  "awaitingCode",
  "streamMode:SYNCING",
  "streamMode:OFFLINE",
]);

const isYellowStatus = (status: string) =>
  yellowStatuses.has(status) || status.startsWith("qrAttempt:");

const greenStatuses = new Set<string>([
  whatsappAccountStatus.connected,
  "connected",
  "isLogged",
  "qrReadSuccess",
  "streamMode:MAIN",
]);

const redStatuses = new Set<string>([
  whatsappAccountStatus.podIsDown,
  "streamMode:CONFLICT",
  "streamMode:PROXYBLOCK",
  "streamMode:TOS_BLOCK",
  "streamMode:SMB_TOS_BLOCK",
  "streamMode:DEPRECATED_VERSION",
  "pairingCodeRateLimited",
  "loginFailed",
  "dailyMessageLimitReached",
]);

const statusEntryColor = (status: string) =>
  greenStatuses.has(status)
    ? "#2e7d32"
    : isYellowStatus(status)
    ? "#f57c00"
    : redStatuses.has(status)
    ? "#ff5555"
    : "#c62828";

const connectingWindowMs = 60_000;

const accountFromHash = () => {
  if (typeof globalThis.location === "undefined") return undefined;
  const [, account] = globalThis.location.hash.replace(/^#/, "").split("/");
  return account ? decodeURIComponent(account) : undefined;
};

const useIsDeepLinkTarget = (identifier: string) => {
  const [isTarget, setIsTarget] = useState(
    () => accountFromHash() === identifier,
  );
  useEffect(() => {
    const onHashChange = () => setIsTarget(accountFromHash() === identifier);
    globalThis.addEventListener("hashchange", onHashChange);
    return () => globalThis.removeEventListener("hashchange", onHashChange);
  }, [identifier]);
  return isTarget;
};

// Expands and scrolls an account card into view when it is the hash target.
const useDeepLinkExpand = (
  identifier: string,
  setExpanded: (expanded: boolean) => void,
) => {
  const ref = useRef<HTMLLIElement>(null);
  const isTarget = useIsDeepLinkTarget(identifier);
  useEffect(() => {
    if (!isTarget) return;
    setExpanded(true);
    ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [isTarget]);
  return ref;
};

const useNow = (intervalMs: number) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
};

const hasReachedDailyLimit = (
  statusHistory: Array<{ status: string; timestamp: number }> | undefined,
  now: number,
) => {
  if (!statusHistory) return false;
  const lastLimitEvent = [...statusHistory]
    .reverse()
    .find((e) => e.status === "dailyMessageLimitReached");
  if (!lastLimitEvent) return false;

  const d1 = new Date(lastLimitEvent.timestamp);
  const d2 = new Date(now);
  return (
    d1.getUTCDate() === d2.getUTCDate() &&
    d1.getUTCMonth() === d2.getUTCMonth() &&
    d1.getUTCFullYear() === d2.getUTCFullYear()
  );
};

const getConnectionDisplay = (
  isConnected: boolean,
  statusHistory: Array<{ status: string; timestamp: number }> | undefined,
  now: number,
) => {
  if (hasReachedDailyLimit(statusHistory, now)) {
    return { label: "Throttled (Limit Reached)", color: "#ff5555" };
  }
  if (isConnected) return { label: "Connected", color: "#2e7d32" };
  const last = statusHistory?.[statusHistory.length - 1];
  if (
    last &&
    (last.status === "streamMode:TOS_BLOCK" ||
      last.status === "streamMode:SMB_TOS_BLOCK")
  ) {
    return { label: "Banned", color: "#ff5555" };
  }
  if (
    last &&
    isYellowStatus(last.status) &&
    now - last.timestamp < connectingWindowMs
  ) {
    return { label: "Connecting", color: "#f57c00" };
  }
  return { label: "Disconnected", color: "#c62828" };
};

const DowntimeDetailsModal = ({
  open,
  onClose,
  phoneNumber,
  name,
  statusHistory,
  lastEventTime,
  now,
}: {
  open: boolean;
  onClose: () => void;
  phoneNumber?: string;
  name?: string;
  statusHistory: Array<{ status: string; timestamp: number }> | undefined;
  lastEventTime: number | null | undefined;
  now: number;
}) => {
  if (!open) return null;

  const downtimeMs = downtimeMsLast24h(statusHistory, now, lastEventTime);
  const uptimePct = uptimePercentageLast24h(statusHistory, now, lastEventTime);
  const bins = downtimeBinsLast24h(statusHistory, now);
  const accountName = name || phoneNumber || "Account";

  const getCategoryColor = (category: "green" | "yellow" | "red" | "gray") => {
    switch (category) {
      case "green":
        return "#2e7d32";
      case "yellow":
        return "#ff9800";
      case "red":
        return "#f44336";
      case "gray":
        return "rgba(255, 255, 255, 0.15)";
    }
  };

  const getCategoryLabel = (category: "green" | "yellow" | "red" | "gray") => {
    switch (category) {
      case "green":
        return "Connected / Operational";
      case "yellow":
        return "Connecting / Transisting";
      case "red":
        return "Disconnected / Down";
      case "gray":
        return "No history";
    }
  };

  const [selectedBinIndex, setSelectedBinIndex] = useState<number | null>(null);

  const windowStart = now - 24 * 60 * 60 * 1000;
  const recentEvents = [...(statusHistory || [])]
    .filter((e) => e.timestamp >= windowStart)
    .reverse();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Connection History (${accountName})`}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Summary metrics */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            background: "rgba(255, 255, 255, 0.03)",
            padding: 12,
            borderRadius: "var(--radius)",
            border: "1px solid var(--border)",
          }}
        >
          <div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              24h Estimated Uptime
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: uptimePct > 99
                  ? "#81c784"
                  : uptimePct > 95
                  ? "#ffb74d"
                  : "#e57373",
              }}
            >
              {uptimePct.toFixed(1)}%
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>24h Total Downtime</div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: downtimeMs > 0 ? "#ffb74d" : "#81c784",
              }}
            >
              {formatDowntime(downtimeMs)}
            </div>
          </div>
        </div>

        {/* 24-hour visual bar */}
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 8,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>24-Hour Timeline</span>
            <span style={{ fontSize: 11, opacity: 0.6 }}>
              Tap an hour to inspect
            </span>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(24, 1fr)",
              gap: 3,
              height: 28,
              background: "rgba(0, 0, 0, 0.2)",
              padding: 4,
              borderRadius: 6,
            }}
          >
            {bins.map((bin, idx) => {
              const isSelected = selectedBinIndex === idx;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setSelectedBinIndex(isSelected ? null : idx)}
                  style={{
                    background: getCategoryColor(bin.category),
                    border: isSelected
                      ? "2px solid #fff"
                      : "1px solid transparent",
                    borderRadius: 2,
                    cursor: "pointer",
                    padding: 0,
                    height: "100%",
                    outline: "none",
                  }}
                  title={`Hour ${idx + 1}/24: ${
                    getCategoryLabel(bin.category)
                  }`}
                />
              );
            })}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 10,
              opacity: 0.5,
              marginTop: 4,
            }}
          >
            <span>24h ago</span>
            <span>12h ago</span>
            <span>Now</span>
          </div>
        </div>

        {/* Selected Hour Details if tapped */}
        {selectedBinIndex !== null && (
          <div
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              padding: 10,
              borderRadius: 6,
              border: "1px solid var(--border)",
              fontSize: 12,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Hour Range:{" "}
              {new Date(bins[selectedBinIndex].start).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })} -{" "}
              {new Date(bins[selectedBinIndex].end).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
            </div>
            <div style={{ opacity: 0.8 }}>
              Status: {getCategoryLabel(bins[selectedBinIndex].category)}
            </div>
            {bins[selectedBinIndex].events.length > 0
              ? (
                <div style={{ marginTop: 6 }}>
                  <div
                    style={{ fontWeight: 600, fontSize: 11, marginBottom: 4 }}
                  >
                    Events in this hour:
                  </div>
                  {bins[selectedBinIndex].events.map((e, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "2px 0",
                        borderBottom: "1px dotted rgba(255, 255, 255, 0.1)",
                      }}
                    >
                      <span
                        style={{
                          color: statusEntryColor(e.status),
                          fontWeight: 500,
                        }}
                      >
                        {e.status}
                      </span>
                      <span style={{ opacity: 0.7 }}>
                        {new Date(e.timestamp).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              )
              : (
                <div style={{ opacity: 0.6, fontSize: 11, marginTop: 4 }}>
                  No status transitions in this hour interval.
                </div>
              )}
          </div>
        )}

        {/* Full 24h Event History Log */}
        <div>
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              marginBottom: 8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>24h Event Log ({recentEvents.length})</span>
            {lastEventTime && (
              <span style={{ fontSize: 11, opacity: 0.6, fontWeight: 400 }}>
                Last event: {new Date(lastEventTime).toLocaleTimeString()}
              </span>
            )}
          </div>

          {recentEvents.length > 0
            ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  maxHeight: 220,
                  overflowY: "auto",
                  padding: 8,
                  background: "rgba(0, 0, 0, 0.2)",
                  borderRadius: "var(--radius)",
                }}
              >
                {recentEvents.map((entry, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: 12,
                      padding: "4px 6px",
                      borderRadius: 4,
                      background: "rgba(255, 255, 255, 0.02)",
                    }}
                  >
                    <span
                      style={{
                        color: statusEntryColor(entry.status),
                        fontWeight: 600,
                      }}
                    >
                      {entry.status}
                    </span>
                    <span style={{ opacity: 0.7, fontSize: 11 }}>
                      {new Date(entry.timestamp).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )
            : (
              <div
                style={{
                  padding: 12,
                  textAlign: "center",
                  fontSize: 12,
                  opacity: 0.6,
                  background: "rgba(0, 0, 0, 0.15)",
                  borderRadius: "var(--radius)",
                }}
              >
                No status changes in the last 24 hours.
              </div>
            )}
        </div>
      </div>
    </Modal>
  );
};

const DowntimeBadge = (
  { phoneNumber, name, statusHistory, lastEventTime, now }: {
    phoneNumber?: string;
    name?: string;
    statusHistory: Array<{ status: string; timestamp: number }> | undefined;
    lastEventTime: number | null | undefined;
    now: number;
  },
) => {
  const [modalOpen, setModalOpen] = useState(false);
  const _downtimeMs = downtimeMsLast24h(statusHistory, now, lastEventTime);
  const uptimePct = uptimePercentageLast24h(statusHistory, now, lastEventTime);
  const bins = downtimeBinsLast24h(statusHistory, now);

  const getCategoryColor = (category: "green" | "yellow" | "red" | "gray") => {
    switch (category) {
      case "green":
        return "#2e7d32";
      case "yellow":
        return "#ff9800";
      case "red":
        return "#f44336";
      case "gray":
        return "rgba(255, 255, 255, 0.15)";
    }
  };

  return (
    <>
      <div
        onClick={(e) => {
          e.stopPropagation();
          setModalOpen(true);
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          marginLeft: "auto",
          padding: "3px 8px",
          borderRadius: "4px",
          background: "rgba(255, 255, 255, 0.04)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: uptimePct > 99
              ? "#81c784"
              : uptimePct > 95
              ? "#ffb74d"
              : "#e57373",
          }}
        >
          {uptimePct.toFixed(1)}% (last 24h)
        </span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {bins.map((bin, idx) => (
            <div
              key={idx}
              style={{
                width: 4,
                height: 14,
                borderRadius: "1px",
                backgroundColor: getCategoryColor(bin.category),
              }}
            />
          ))}
        </div>
      </div>

      <DowntimeDetailsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        phoneNumber={phoneNumber}
        name={name}
        statusHistory={statusHistory}
        lastEventTime={lastEventTime}
        now={now}
      />
    </>
  );
};

const formatStatusHistory = (
  history: Array<{ status: string; timestamp: number }> | undefined,
) => {
  const [showHistory, setShowHistory] = useState(false);
  if (!history || history.length === 0) return null;
  return (
    <div style={{ marginTop: 10, fontSize: 12 }}>
      <button
        type="button"
        onClick={() => setShowHistory(!showHistory)}
        style={{
          background: "none",
          border: "none",
          color: "inherit",
          cursor: "pointer",
          fontWeight: 600,
          fontSize: 12,
          padding: 0,
          opacity: 0.9,
          textDecoration: "underline",
        }}
      >
        {showHistory ? "Hide" : "Show"} Status History
      </button>
      {showHistory && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            maxHeight: "200px",
            overflowY: "auto",
            padding: "8px",
            background: "rgba(0,0,0,0.2)",
            borderRadius: "var(--radius)",
            marginTop: 6,
          }}
        >
          {[...history].reverse().map((entry, idx) => (
            <div key={idx} style={{ opacity: 0.85, fontSize: 11 }}>
              <span
                style={{
                  color: statusEntryColor(entry.status),
                  fontWeight: 500,
                }}
              >
                {entry.status}
              </span>{" "}
              <span style={{ opacity: 0.7 }}>
                {new Date(entry.timestamp).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const WaAccount = (
  {
    account: {
      id,
      admins,
      secretToken,
      webhook,
      verificationCode,
      pairingCode,
      qrIssuedAt,
      webhookUrls,
      name,
      notes,
      lastEventTime,
      preferCodeLogin,
      nextWarmupAt: _nextWarmupAt,
      isEnabled,
      staticProxy,
      statusHistory,
      phoneNumber,
      isConnected,
      avatarUrl,
    },
  }: {
    account: InstaQLEntity<
      typeof schema,
      "whatsappAccounts",
      // deno-lint-ignore ban-types
      { server: {}; admins: {}; staticProxy: {} }
    >;
  },
) => {
  const { user: authUser } = useAuth();
  const { dbUser } = useUser();
  const now = useNow(5000);
  const lastStatus = statusHistory?.[statusHistory.length - 1]
    ?.status;
  const isReconnecting = useIsReconnecting(phoneNumber);
  const isBusy = isReconnecting || !!(lastStatus && [
    "provisioningServer",
    whatsappAccountStatus.waitingForSsh,
    "startingDocker",
  ].includes(lastStatus));
  const [expanded, setExpanded] = useState(false);
  const waCardRef = useDeepLinkExpand(phoneNumber, setExpanded);
  const [availableProxyCountries, setAvailableProxyCountries] = useState<
    string[]
  >([]);
  const currentProxyCountry = staticProxy?.country;
  const canChangeProxyCountry = !isConnected;
  const changeProxyCountry = async () => {
    if (!authUser?.refresh_token) {
      toast.error("Not authenticated");
      return;
    }
    const countries = availableProxyCountries.length > 0
      ? availableProxyCountries
      : (await userClient({
        endpoint: "getAvailableProxyCountries",
        payload: {
          refreshToken: authUser.refresh_token,
          phoneNumber,
        },
      }).then((res) => {
        if ("error" in res) throw new Error(String(res.error));
        setAvailableProxyCountries(res.countries);
        return res.countries;
      }));
    if (countries.length === 0) {
      toast.error("No proxy countries currently available");
      return;
    }
    const country = (await promptDialog(
      `Choose proxy country (${countries.join(", ")}):`,
      {
        title: "Set proxy country",
        defaultValue: currentProxyCountry ?? countries[0],
        placeholder: countries.join(", "),
      },
    ))?.trim().toUpperCase();
    if (!country) return;
    if (!countries.includes(country)) {
      toast.error(`No available proxies in ${country}`);
      return;
    }
    await toast.promise(
      userClient({
        endpoint: "setProxyCountry",
        payload: {
          refreshToken: authUser.refresh_token,
          phoneNumber,
          country,
        },
      }).then((res) => {
        if ("error" in res) throw new Error(String(res.error));
        return res;
      }),
      {
        loading: "Changing proxy country...",
        success: `Proxy country changed to ${country}`,
        error: (e) => e?.message || "Failed to change proxy country",
      },
    );
  };
  return (
    <li
      key={id}
      ref={waCardRef}
      style={{
        border: "1px solid var(--border)",
        borderRadius: "var(--radius)",
        padding: 12,
        background: "var(--surface-alt)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <span style={{ fontSize: 12, opacity: 0.5 }}>
          {expanded ? "▼" : "▶"}
        </span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontWeight: 600,
          }}
        >
          {avatarUrl
            ? (
              <img
                src={avatarUrl}
                alt=""
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            )
            : (
              <div
                title="If your avatar isn't showing, change your WhatsApp privacy settings for 'Profile Photo' to 'Everyone' so our workers can fetch it."
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: "var(--surface-alt)",
                  border: "1px dashed var(--border)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  color: "var(--text-muted)",
                  cursor: "help",
                }}
              >
                👤
              </div>
            )}
          {name || phoneNumber}
        </div>
        <span
          style={{
            color: getConnectionDisplay(!!isConnected, statusHistory, now)
              .color,
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          {getConnectionDisplay(!!isConnected, statusHistory, now).label}
        </span>
        <DowntimeBadge
          phoneNumber={phoneNumber}
          name={name}
          statusHistory={statusHistory}
          lastEventTime={lastEventTime}
          now={now}
        />
        {isEnabled === false && (
          <span
            style={{
              color: "#ff9800",
              fontWeight: 600,
              fontSize: 12,
              border: "1px solid #ff9800",
              padding: "2px 6px",
              borderRadius: "4px",
            }}
          >
            SUSPENDED
          </span>
        )}
      </div>
      {expanded && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 14, opacity: 0.85 }}>
            Number: {phoneNumber}
          </div>
          <div style={{ fontSize: 14, opacity: 0.85, marginTop: 4 }}>
            Proxy country: {currentProxyCountry ?? "Automatic"}
          </div>
          <Button
            className="secondary"
            style={{ fontSize: 12, padding: "2px 8px", marginTop: 4 }}
            onClick={() => editAccountName(id, name)}
          >
            ✏ Edit Name
          </Button>
          {lastEventTime && (
            <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
              Last event: {new Date(lastEventTime).toLocaleString()}
            </div>
          )}
          {formatStatusHistory(statusHistory)}
          <details style={{ marginTop: 12 }}>
            <summary
              style={{
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 13,
                userSelect: "none",
              }}
            >
              Advanced
            </summary>
            <div
              style={{
                marginTop: 8,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {secretToken && <SecretToken token={secretToken} />}
              <WebhookEditor
                phoneNumber={phoneNumber}
                webhook={webhook}
                webhookUrls={webhookUrls}
              />
              <div
                style={{
                  fontSize: 12,
                  opacity: 0.85,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span>Admins:</span>
                {(admins && admins.length > 0)
                  ? admins.map((admin) => (
                    <span style={adminChipStyle}>
                      {admin.email}
                      <Button
                        className="secondary"
                        style={{ fontSize: 10, padding: "2px 6px" }}
                        onClick={() => removeAdmin(id, admin.id, admin.email)}
                      >
                        🗑 Remove
                      </Button>
                    </span>
                  ))
                  : <span>None</span>}
                <Button
                  className="secondary"
                  style={{ fontSize: 12, padding: "4px 10px" }}
                  onClick={() =>
                    addAdmin(
                      id,
                      (admins || []).map((
                        a: { email: string },
                      ) => a.email),
                    )}
                >
                  + Add Admin
                </Button>
              </div>
            </div>
          </details>
          <div
            style={{
              marginTop: 6,
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: 12,
                opacity: 0.85,
                whiteSpace: "pre-wrap",
              }}
            >
              {notes ? `Notes: ${notes}` : "No notes"}
            </span>
            <Button
              className="secondary"
              style={{ fontSize: 10, padding: "2px 6px" }}
              onClick={() => editAccountNotes(id, notes)}
            >
              {notes ? "✏ Edit Notes" : "+ Add Notes"}
            </Button>
          </div>
          {!isConnected &&
            verificationCode && (
            <div style={{ marginTop: 10 }}>
              <QrWithTimer
                size={180}
                url={verificationCode}
                issuedAt={coerce(qrIssuedAt)}
              />
              <div
                style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}
              >
                Scan with WhatsApp on your phone
              </div>
            </div>
          )}
          {!isConnected &&
            pairingCode && (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  fontSize: 32,
                  fontWeight: 600,
                  letterSpacing: 8,
                  padding: "20px",
                  border: "2px solid var(--border)",
                  borderRadius: "var(--radius)",
                  background: "var(--surface-alt)",
                  textAlign: "center",
                  fontFamily: "monospace",
                }}
              >
                {pairingCode}
              </div>
              <div
                style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}
              >
                Enter this code in WhatsApp: Settings → Linked Devices → Link a
                Device → Link with phone number
              </div>
            </div>
          )}
          {!isConnected &&
            !verificationCode &&
            !pairingCode && (
            <div
              style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}
            >
              {lastStatus === "pairingCodeRateLimited"
                ? (
                  <span style={{ color: "#ff5555", fontWeight: 600 }}>
                    WhatsApp rate-limited pairing code requests. Wait a few
                    minutes and try again, or switch to QR code login.
                  </span>
                )
                : lastStatus === "streamMode:TOS_BLOCK" ||
                    lastStatus === "streamMode:SMB_TOS_BLOCK"
                ? (
                  <span style={{ color: "#ff5555", fontWeight: 600 }}>
                    This WhatsApp account has been banned/blocked by WhatsApp
                    (TOS Block).
                  </span>
                )
                : "Waiting for QR code or pairing code..."}
            </div>
          )}
          <div
            style={{
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <label style={{ fontSize: 12, opacity: 0.8 }}>
              <input
                type="checkbox"
                checked={preferCodeLogin ?? false}
                onChange={(e) =>
                  setLoginMethod(
                    phoneNumber,
                    e.currentTarget.checked,
                  )}
                style={{ marginRight: 6 }}
              />
              Prefer pairing code (instead of QR)
            </label>
          </div>
          <div
            style={{
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <label
              style={{ fontSize: 12, opacity: 0.5, cursor: "not-allowed" }}
            >
              <input
                type="checkbox"
                checked={false}
                disabled
                style={{ marginRight: 6 }}
              />
              Participate in warmup network (Disabled)
            </label>
          </div>
          <div
            style={accountActionRowStyle}
          >
            <Button
              className="primary"
              disabled={!isConnected}
              onClick={() => sendTest(phoneNumber, dbUser?.whatsapp)}
            >
              ▶ Send test
            </Button>
            <Button
              className="secondary"
              disabled={isBusy}
              onClick={() => reconnect(phoneNumber)}
            >
              {preferCodeLogin ? "📨 Send Login Code" : "🔳 Send QR Code"}
            </Button>
            <Button
              className="secondary"
              disabled={!canChangeProxyCountry || isBusy}
              onClick={changeProxyCountry}
            >
              🌐 Change Proxy Country
            </Button>
            <Button
              className="secondary"
              onClick={() => changeAccountName(phoneNumber)}
            >
              ✏️ Change Name
            </Button>
            <Button
              className="secondary"
              onClick={() => changeAccountAvatar(phoneNumber)}
            >
              🖼️ Change Avatar
            </Button>
            <Button
              className="secondary"
              onClick={() => changeAccountBio(phoneNumber)}
            >
              💬 Change Bio
            </Button>
            <Button
              className="secondary"
              onClick={() => changeAccountPrivacy(phoneNumber)}
            >
              🔒 Privacy Settings
            </Button>
            <Button
              className="secondary"
              disabled={isBusy}
              onClick={async () => {
                if (
                  !await confirmDialog(
                    "Are you sure you want to clear all credentials for this WhatsApp number? This cannot be undone.",
                    {
                      title: "Reset WhatsApp credentials",
                      okLabel: "Reset",
                      danger: true,
                    },
                  )
                ) return;
                const token = await getToken(phoneNumber);
                const promise = client({
                  endpoint: "clearCredentials",
                  payload: { phoneNumber, token },
                }).then((res) => {
                  if (!res?.success) {
                    throw new Error(
                      res?.error || "Failed to clear credentials",
                    );
                  }
                  return res;
                });
                await toast.promise(promise, {
                  loading: "Clearing credentials...",
                  success: "Credentials cleared",
                  error: (e) => e?.message || "Failed to clear credentials",
                });
              }}
            >
              🔄 Reset
            </Button>
            <Button
              className="secondary"
              style={{
                color: "var(--error)",
                borderColor: "var(--error)",
              }}
              onClick={async () => {
                if (!authUser) {
                  await alertDialog("Not authenticated", {
                    title: "Not authenticated",
                  });
                  return;
                }
                if (
                  !await confirmDialog(
                    "Are you sure you want to DELETE this account? This will stop the server and remove all data.",
                    {
                      title: "Delete WhatsApp account",
                      okLabel: "Delete",
                      danger: true,
                    },
                  )
                ) return;

                await toast.promise(
                  userClient({
                    endpoint: "deleteAccount",
                    payload: {
                      refreshToken: authUser.refresh_token,
                      phoneNumber,
                    },
                  }).then((res) => {
                    if ("error" in res) throw new Error(String(res.error));
                    return res;
                  }),
                  {
                    loading: "Deleting account...",
                    success: "Account deleted",
                    error: (e) => e.message || "Failed to delete account",
                  },
                );
              }}
            >
              ❌ Delete
            </Button>
          </div>
        </div>
      )}
    </li>
  );
};

const CreateWhatsappAccountModal = (
  { open, onClose }: { open: boolean; onClose: () => void },
) => {
  const { user: authUser } = useAuth();
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setPhone("");
    setName("");
    setNotes("");
    setErrors({});
    setSubmitting(false);
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    const phoneNumber = phone.replace(/\D/g, "");
    if (!phoneNumber) {
      nextErrors.phone = "Phone number is required";
    } else if (phoneNumber.length < 10 || phoneNumber.length > 15) {
      nextErrors.phone =
        "Phone number must be 10-15 digits in international format (e.g. 15551234567)";
    } else if (phoneNumber.startsWith("0")) {
      nextErrors.phone =
        "Please use international format without leading zero (e.g. 1...)";
    } else {
      try {
        const parsed = parsePhoneNumber("+" + phoneNumber);
        if (!parsed || !parsed.isValid()) {
          nextErrors.phone =
            `Phone number +${phoneNumber} is not a valid international number according to telecom standards`;
        }
      } catch (_e) {
        nextErrors.phone =
          `Phone number +${phoneNumber} is not a valid international number according to telecom standards`;
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0
      ? { phoneNumber, name: name.trim(), notes: notes.trim() }
      : null;
  };

  const handleSubmit = async () => {
    const valid = validate();
    if (!valid) return;
    if (!authUser?.refresh_token) {
      toast.error("Not authenticated");
      return;
    }
    setSubmitting(true);
    try {
      const res = await userClient({
        endpoint: "createAccount",
        payload: {
          refreshToken: authUser.refresh_token,
          phoneNumber: valid.phoneNumber,
          name: valid.name || undefined,
          notes: valid.notes || undefined,
        },
      });
      if ("error" in res) throw new Error(String(res.error));
      toast.success("Account added");
      reset();
      onClose();
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : `Failed to add account. Please contact support@supergreen.cc`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add WhatsApp account">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={formGroupStyle}>
          <label style={labelStyle}>WhatsApp phone number</label>
          <input
            type="text"
            style={inputStyle}
            placeholder="e.g. 15551234567"
            value={phone}
            onInput={(e) => setPhone(e.currentTarget.value)}
          />
          <div style={helpTextStyle}>
            Do not connect your personal number unless you know what you're
            doing — blocks may occur. AI agents and automations work best with
            dedicated WhatsApp accounts.
          </div>
          {errors.phone && <div style={errorTextStyle}>{errors.phone}</div>}
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Account Name</label>
          <input
            type="text"
            style={inputStyle}
            placeholder="e.g. Customer Support"
            value={name}
            onInput={(e) => setName(e.currentTarget.value)}
          />
          <div style={helpTextStyle}>
            A friendly name to identify this account.
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Notes</label>
          <textarea
            style={{ ...inputStyle, minHeight: 60, fontFamily: "inherit" }}
            placeholder="e.g. Used for marketing campaigns"
            value={notes}
            onInput={(e) => setNotes(e.currentTarget.value)}
          />
          <div style={helpTextStyle}>
            Any additional details or context for this account.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 8,
          }}
        >
          <Button
            className="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            className="primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Adding..." : "Add Account"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const WhatsappAccounts = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const { data, error, isLoading } = useQuery({
    whatsappAccounts: {
      admins: {},
      server: {},
      staticProxy: {},
    },
  });
  if (error) return <ErrorMessage error={error} />;
  if (isLoading) return <LoadingIndication text="Loading bots..." />;
  const whatsappAccounts = [...data.whatsappAccounts].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "")
  );

  return (
    <div
      style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h2 style={{ margin: 0 }}>Your whatsapp accounts</h2>
        <Button
          className="primary"
          style={{ fontSize: 12, padding: "4px 12px" }}
          onClick={() => setModalOpen(true)}
        >
          + Add
        </Button>
      </div>
      <CreateWhatsappAccountModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
      <ConnectHelp />
      {whatsappAccounts.length === 0 ? <div>No bots yet.</div> : (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {whatsappAccounts.map((account) => (
            <WaAccount key={account.id} account={account} />
          ))}
        </ul>
      )}
    </div>
  );
};

const TgAccount = (
  {
    account: {
      id,
      admins,
      secretToken,
      webhook,
      webhookUrls,
      name,
      sessionString,
      statusHistory,
      phoneNumber,
      isConnected,
      isEnabled,
      lastEventTime,
    },
  }: {
    account: InstaQLEntity<
      typeof schema,
      "telegramAccounts",
      // deno-lint-ignore ban-types
      { admins: {} }
    >;
  },
) => {
  const { user: authUser } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const tgCardRef = useDeepLinkExpand(phoneNumber, setExpanded);
  const [codeInput, setCodeInput] = useState("");
  const now = useNow(5000);
  const lastStatusEntry = statusHistory?.[statusHistory.length - 1];
  const lastStatus = lastStatusEntry?.status;
  const awaitingCode = !sessionString;

  return (
    <li
      key={id}
      ref={tgCardRef}
      style={{
        border: "1px solid var(--border)",
        borderRadius: "var(--radius)",
        padding: 12,
        background: "var(--surface-alt)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <span style={{ fontSize: 12, opacity: 0.5 }}>
          {expanded ? "▼" : "▶"}
        </span>
        <div style={{ fontWeight: 600 }}>
          {name || phoneNumber}
        </div>
        <span
          style={{
            color: getConnectionDisplay(!!isConnected, statusHistory, now)
              .color,
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          {getConnectionDisplay(!!isConnected, statusHistory, now).label}
        </span>
        {awaitingCode && (
          <span
            style={{
              color: "#2196f3",
              fontWeight: 600,
              fontSize: 12,
              border: "1px solid #2196f3",
              padding: "2px 6px",
              borderRadius: "4px",
            }}
          >
            AWAITING CODE
          </span>
        )}
        {isEnabled === false && (
          <span
            style={{
              color: "#ff9800",
              fontWeight: 600,
              fontSize: 12,
              border: "1px solid #ff9800",
              padding: "2px 6px",
              borderRadius: "4px",
            }}
          >
            SUSPENDED
          </span>
        )}
      </div>
      {expanded && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 14, opacity: 0.85 }}>
            Number: {phoneNumber}
          </div>
          {lastStatus && (
            <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
              Last status: {lastStatus}
            </div>
          )}
          {lastEventTime
            ? (
              <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
                Last event: {new Date(lastEventTime).toLocaleString()}
              </div>
            )
            : lastStatusEntry?.timestamp
            ? (
              <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
                Last event:{" "}
                {new Date(lastStatusEntry.timestamp).toLocaleString()}
              </div>
            )
            : null}
          {formatStatusHistory(statusHistory)}
          {awaitingCode && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                background: "var(--surface)",
                borderRadius: "var(--radius)",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                Check your Telegram app for a login code, then enter it below:
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  placeholder="Enter code"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.currentTarget.value)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: "var(--radius)",
                    border: "1px solid var(--border)",
                    background: "var(--surface)",
                    color: "var(--text)",
                    fontSize: 14,
                    flex: 1,
                  }}
                />
                <Button
                  className="primary"
                  onClick={async () => {
                    if (!authUser?.refresh_token) {
                      toast.error("Not authenticated");
                      return;
                    }
                    const trimmed = codeInput.trim();
                    if (!trimmed) {
                      toast.error("Please enter a code");
                      return;
                    }
                    const promise = userClient({
                      endpoint: "submitTelegramCode",
                      payload: {
                        refreshToken: authUser.refresh_token,
                        phoneNumber,
                        code: trimmed,
                      },
                    }).then((res) => {
                      if ("error" in res) throw new Error(String(res.error));
                      return res;
                    });
                    await toast.promise(promise, {
                      loading: "Submitting code...",
                      success: "Code submitted",
                      error: (e) => e?.message || "Failed to submit code",
                    });
                    setCodeInput("");
                  }}
                >
                  Submit Code
                </Button>
              </div>
            </div>
          )}
          <details style={{ marginTop: 12 }}>
            <summary
              style={{
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 13,
                userSelect: "none",
              }}
            >
              Advanced
            </summary>
            <div
              style={{
                marginTop: 8,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {secretToken && <SecretToken token={secretToken} />}
              <TgWebhookEditor
                phoneNumber={phoneNumber}
                webhook={webhook}
                webhookUrls={webhookUrls}
              />
              <div
                style={{
                  fontSize: 12,
                  opacity: 0.85,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span>Admins:</span>
                {(admins && admins.length > 0)
                  ? admins.map((admin) => (
                    <span style={adminChipStyle}>
                      {admin.email}
                      <Button
                        className="secondary"
                        style={{ fontSize: 10, padding: "2px 6px" }}
                        onClick={() => removeAdmin(id, admin.id, admin.email)}
                      >
                        🗑 Remove
                      </Button>
                    </span>
                  ))
                  : <span>None</span>}
                <Button
                  className="secondary"
                  style={{ fontSize: 12, padding: "4px 10px" }}
                  onClick={() =>
                    addAdmin(
                      id,
                      (admins || []).map((
                        a: { email: string },
                      ) => a.email),
                    )}
                >
                  + Add Admin
                </Button>
              </div>
            </div>
          </details>
          <div style={accountActionRowStyle}>
            <Button
              className="primary"
              onClick={() => sendTelegramTest(phoneNumber)}
            >
              ▶ Send test
            </Button>
            <Button
              className="secondary"
              onClick={() => listTelegramGroups(phoneNumber)}
            >
              📋 List groups
            </Button>
            <Button
              className="secondary"
              onClick={() => changeAccountName(phoneNumber)}
            >
              ✏️ Change Name
            </Button>
            <Button
              className="secondary"
              style={{ color: "var(--error)", borderColor: "var(--error)" }}
              onClick={async () => {
                if (!authUser) {
                  await alertDialog("Not authenticated", {
                    title: "Not authenticated",
                  });
                  return;
                }
                if (
                  !await confirmDialog(
                    "Are you sure you want to DELETE this Telegram account?",
                    {
                      title: "Delete Telegram account",
                      okLabel: "Delete",
                      danger: true,
                    },
                  )
                ) return;
                await toast.promise(
                  userClient({
                    endpoint: "deleteTelegramAccount",
                    payload: {
                      refreshToken: authUser.refresh_token,
                      phoneNumber,
                    },
                  }).then((res) => {
                    if ("error" in res) throw new Error(String(res.error));
                    return res;
                  }),
                  {
                    loading: "Deleting account...",
                    success: "Account deleted",
                    error: (e) => e.message || "Failed to delete account",
                  },
                );
              }}
            >
              ❌ Delete
            </Button>
          </div>
        </div>
      )}
    </li>
  );
};

const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  borderRadius: "var(--radius)",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text)",
  fontSize: 14,
  boxSizing: "border-box" as const,
};

const labelStyle = {
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 4,
  display: "block",
};

const helpTextStyle = {
  fontSize: 12,
  opacity: 0.7,
  marginTop: 4,
};

const errorTextStyle = {
  fontSize: 12,
  color: "#ff5555",
  marginTop: 4,
};

const formGroupStyle = {
  display: "flex",
  flexDirection: "column" as const,
  gap: 2,
};

const normalizeTelegramPhone = (input: string) => {
  const digits = input.replace(/\D/g, "");
  if (digits.startsWith("0")) return digits.slice(1);
  return digits;
};

const CreateTelegramAccountModal = (
  { open, onClose }: { open: boolean; onClose: () => void },
) => {
  const { user: authUser } = useAuth();
  const [phone, setPhone] = useState("");
  const [apiId, setApiId] = useState("");
  const [apiHash, setApiHash] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setPhone("");
    setApiId("");
    setApiHash("");
    setErrors({});
    setSubmitting(false);
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    const normalizedPhone = normalizeTelegramPhone(phone);
    if (!normalizedPhone) {
      nextErrors.phone = "Phone number is required";
    } else if (normalizedPhone.length < 10 || normalizedPhone.length > 15) {
      nextErrors.phone =
        "Must be 10-15 digits in international format (e.g. 15551234567)";
    }
    const idNum = Number(apiId);
    if (!apiId.trim()) {
      nextErrors.apiId = "API ID is required";
    } else if (!idNum || isNaN(idNum)) {
      nextErrors.apiId = "API ID must be a number";
    }
    if (!apiHash.trim()) {
      nextErrors.apiHash = "API Hash is required";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0
      ? { phoneNumber: normalizedPhone, apiId: idNum, apiHash: apiHash.trim() }
      : null;
  };

  const handleSubmit = async () => {
    const valid = validate();
    if (!valid) return;
    if (!authUser?.refresh_token) {
      toast.error("Not authenticated");
      return;
    }
    setSubmitting(true);
    try {
      const res = await userClient({
        endpoint: "createTelegramAccount",
        payload: {
          refreshToken: authUser.refresh_token,
          phoneNumber: valid.phoneNumber,
          apiId: valid.apiId,
          apiHash: valid.apiHash,
        },
      });
      if ("error" in res) throw new Error(String(res.error));
      toast.success("Account added! Check your Telegram app for a login code.");
      reset();
      onClose();
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : "Failed to add account. Please contact support@supergreen.cc",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Telegram Account">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={formGroupStyle}>
          <label style={labelStyle}>Your Telegram phone number</label>
          <input
            type="text"
            style={inputStyle}
            placeholder="e.g. 15551234567"
            value={phone}
            onChange={(e) => setPhone(e.currentTarget.value)}
          />
          <div style={helpTextStyle}>
            The phone number of your existing Telegram account, in international
            format without leading zero.
          </div>
          {errors.phone && <div style={errorTextStyle}>{errors.phone}</div>}
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Telegram API ID</label>
          <input
            type="text"
            style={inputStyle}
            placeholder="e.g. 12345678"
            value={apiId}
            onChange={(e) => setApiId(e.currentTarget.value)}
          />
          <div style={helpTextStyle}>
            <ol
              style={{
                margin: "4px 0 0 16px",
                padding: 0,
                fontSize: 12,
                opacity: 0.85,
              }}
            >
              <li>
                Go to{" "}
                <a
                  href="https://my.telegram.org/apps"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--primary)" }}
                >
                  my.telegram.org/apps
                </a>{" "}
                and log in with your Telegram account
              </li>
              <li>Fill "App title" and "Short name" (anything works)</li>
              <li>Click "Create application"</li>
              <li>
                Copy the <strong>api_id</strong> number here
              </li>
            </ol>
          </div>
          {errors.apiId && <div style={errorTextStyle}>{errors.apiId}</div>}
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Telegram API Hash</label>
          <input
            type="text"
            style={inputStyle}
            placeholder="e.g. a1b2c3d4e5f6..."
            value={apiHash}
            onChange={(e) => setApiHash(e.currentTarget.value)}
          />
          <div style={helpTextStyle}>
            From the same page on{" "}
            <a
              href="https://my.telegram.org/apps"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--primary)" }}
            >
              my.telegram.org
            </a>
            . Copy the <strong>api_hash</strong>{" "}
            string here. Treat this like a password — never share it.
          </div>
          {errors.apiHash && <div style={errorTextStyle}>{errors.apiHash}</div>}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 8,
          }}
        >
          <Button
            className="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            className="primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Adding..." : "Add Account"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const TelegramAccounts = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const { data, error, isLoading } = useQuery({
    telegramAccounts: {
      admins: {},
    },
  });
  if (error) return <ErrorMessage error={error} />;
  if (isLoading) {
    return <LoadingIndication text="Loading Telegram accounts..." />;
  }
  const telegramAccounts = data.telegramAccounts;

  return (
    <div
      style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h2 style={{ margin: 0 }}>Your Telegram accounts</h2>
        <Button
          className="primary"
          style={{ fontSize: 12, padding: "4px 12px" }}
          onClick={() => setModalOpen(true)}
        >
          + Add
        </Button>
      </div>
      <CreateTelegramAccountModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
      {telegramAccounts.length === 0
        ? <div>No Telegram accounts yet.</div>
        : (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {telegramAccounts.map((account) => (
              <TgAccount key={account.id} account={account} />
            ))}
          </ul>
        )}
    </div>
  );
};

const isoFlagEmoji = (iso: string) => {
  if (!iso || iso.length !== 2) return "🌐";
  const base = 0x1f1e6;
  const A = "A".charCodeAt(0);
  return String.fromCodePoint(
    base + iso.toUpperCase().charCodeAt(0) - A,
    base + iso.toUpperCase().charCodeAt(1) - A,
  );
};

const addPbxmeNumber = (refreshToken: string) => async () => {
  const loadingId = toast.loading("Loading countries...");
  let countries: Array<
    {
      countryId: string;
      iso: string;
      name: string;
      priceUsd: number;
      available: number;
    }
  >;
  try {
    const res = await userClient({
      endpoint: "listPbxmeCountries",
      payload: { refreshToken },
    });
    toast.dismiss(loadingId);
    if ("error" in res) {
      toast.error(String(res.error));
      return;
    }
    countries = res.countries;
  } catch (e) {
    toast.dismiss(loadingId);
    toast.error((e as Error)?.message || "Failed to load countries");
    return;
  }
  if (countries.length === 0) {
    await alertDialog("No SMS-capable countries are available right now.", {
      title: "Unavailable",
    });
    return;
  }
  const options = countries.map((c) => ({
    value: c.countryId,
    label: `${isoFlagEmoji(c.iso)}  ${c.name}`,
    description: "Click to check availability & pricing",
  }));
  const israel = countries.find((c) => c.countryId === "93");
  const chosen = await selectDialog(options, {
    title: "Choose a country",
    message: "Pick a country for your new SMS-capable phone number.",
    defaultValue: israel ? "93" : countries[0].countryId,
    okLabel: "Check Availability",
    searchable: true,
  });
  if (!chosen) return;

  const country = countries.find((c) => c.countryId === chosen);
  const checkingId = toast.loading(
    `Checking availability for ${country?.name || "the country"}...`,
  );
  let availabilityRes;
  try {
    availabilityRes = await userClient({
      endpoint: "getPbxmeCountryAvailability",
      payload: { refreshToken, countryId: chosen },
    });
    toast.dismiss(checkingId);
    if ("error" in availabilityRes) {
      toast.error(String(availabilityRes.error));
      return;
    }
  } catch (e) {
    toast.dismiss(checkingId);
    toast.error((e as Error)?.message || "Failed to check availability");
    return;
  }

  if (availabilityRes.available === 0) {
    await alertDialog(
      `No numbers are currently available for ${
        country?.name || "this country"
      }. Please pick another country.`,
      { title: "No numbers available" },
    );
    return;
  }

  const confirmMsg = `Found ${availabilityRes.available} available number${
    availabilityRes.available === 1 ? "" : "s"
  } for ${country?.name || "this country"}.
${
    availabilityRes.sampleNumber
      ? `Sample number: +${availabilityRes.sampleNumber}`
      : ""
  }
Pricing: $${availabilityRes.priceUsd}/mo.

Add this new phone number for SMS?`;

  if (
    !await confirmDialog(confirmMsg, {
      title: "Confirm purchase",
      okLabel: "Buy number",
    })
  ) return;

  const promise = userClient({
    endpoint: "createPbxmeNumber",
    payload: { refreshToken, countryId: chosen },
  }).then((res) => {
    if ("error" in res) throw new Error(String(res.error));
    return res;
  });
  await toast.promise(promise, {
    loading: "Allocating number...",
    success: (r) => `Number +${r.phone} added`,
    error: (e) => e?.message || "Failed to add number.",
  });
};

const releasePhoneNumber =
  (refreshToken: string, phoneNumber: string) => async () => {
    if (
      !await confirmDialog(
        `Release +${phoneNumber}? This permanently releases the number.`,
        { title: "Release number", okLabel: "Release", danger: true },
      )
    ) return;
    const promise = userClient({
      endpoint: "releaseNumber",
      payload: { refreshToken, phoneNumber },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    await toast.promise(promise, {
      loading: "Releasing number...",
      success: "Number released",
      error: (e) => e?.message || "Failed to release number.",
    });
  };

const notAuthenticated = () => {
  toast.error("Not authenticated");
};

const fbAccountContainerStyle = {
  padding: 20,
  display: "flex",
  flexDirection: "column",
  gap: 12,
} as const;

const manageBillingNoticeStyle = {
  fontSize: 13,
  opacity: 0.85,
  lineHeight: 1.5,
} as const;

const manageBillingListStyle = {
  margin: 0,
  paddingLeft: 18,
  fontSize: 13,
  opacity: 0.9,
  display: "flex",
  flexDirection: "column",
  gap: 2,
} as const;

const manageBillingActionsStyle = {
  display: "flex",
  gap: 8,
  justifyContent: "flex-end",
  marginTop: 16,
} as const;

type ManageBillingModalProps = {
  open: boolean;
  onClose: () => void;
  refreshToken: string | undefined;
  waCount: number;
  tgCount: number;
  fbCount: number;
  numberCount: number;
};

const ManageBillingModal = (
  {
    open,
    onClose,
    refreshToken,
    waCount,
    tgCount,
    fbCount,
    numberCount,
  }: ManageBillingModalProps,
) => {
  const proceed = async () => {
    if (!refreshToken) {
      toast.error("Not authenticated");
      return;
    }
    await openBillingPortal(refreshToken);
  };
  return (
    <Modal open={open} onClose={onClose} title="Manage subscription">
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={manageBillingNoticeStyle}>
          You'll be redirected to Stripe to edit or cancel your subscriptions.
          When your paid capacity drops below what you're currently using, the
          following services will be disabled automatically. Their credentials
          stay safe — resubscribe at any time to bring them back.
        </div>
        <div style={manageBillingNoticeStyle}>
          Currently active:
        </div>
        <ul style={manageBillingListStyle}>
          {waCount > 0 && (
            <li>{waCount} WhatsApp account{waCount === 1 ? "" : "s"}</li>
          )}
          {tgCount > 0 && (
            <li>{tgCount} Telegram account{tgCount === 1 ? "" : "s"}</li>
          )}
          {fbCount > 0 && (
            <li>{fbCount} Facebook account{fbCount === 1 ? "" : "s"}</li>
          )}
          {numberCount > 0 && (
            <li>
              {numberCount} phone number{numberCount === 1 ? "" : "s"}{" "}
              (released immediately if cancelled)
            </li>
          )}
          {waCount + tgCount + fbCount + numberCount === 0 && (
            <li>No active services.</li>
          )}
        </ul>
        <div style={manageBillingNoticeStyle}>
          If you'd rather pick which accounts to keep, close this dialog and
          delete the ones you don't want first.
        </div>
        <div style={manageBillingActionsStyle}>
          <Button className="secondary" onClick={onClose}>Cancel</Button>
          <Button className="primary" onClick={proceed}>
            Continue to Stripe
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const FbAccountCard = ({
  account,
  activeLoginSessions,
  openFbLogin,
  finishFbLogin,
  releaseFbAccount,
  addGroup,
  removeGroup,
  saveWebhooks,
}: {
  account: {
    id: string;
    status?: string | null;
    kernelProfileName?: string | null;
    secretToken?: string | null;
    lastEventTime?: number | null;
    webhookUrls?: string[] | null;
    fbGroups?: Array<{
      id: string;
      groupId: string;
      name?: string;
      lastScrapedAt?: number;
      lastEventTime?: number;
    }>;
  };
  activeLoginSessions: Record<string, string>;
  openFbLogin: (id: string) => Promise<void>;
  finishFbLogin: (id: string) => Promise<void>;
  releaseFbAccount: (id: string) => Promise<void>;
  addGroup: (id: string, groupId: string, name?: string) => Promise<void>;
  removeGroup: (groupId: string) => Promise<void>;
  saveWebhooks: (id: string, urls: string) => Promise<void>;
}) => {
  const [expanded, setExpanded] = useState(false);
  const fbCardRef = useDeepLinkExpand(account.id, setExpanded);
  const [newGroupId, setNewGroupId] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [webhookUrlsText, setWebhookUrlsText] = useState(
    (account.webhookUrls || []).join("\n"),
  );

  const isLoginRequired = account.status === "loginRequired";

  const handleAddGroup = async () => {
    if (!newGroupId.trim()) return;
    await addGroup(
      account.id,
      newGroupId.trim(),
      newGroupName.trim() || undefined,
    );
    setNewGroupId("");
    setNewGroupName("");
  };

  return (
    <li
      ref={fbCardRef}
      style={{
        border: "1px solid var(--border)",
        borderRadius: "var(--radius)",
        padding: 12,
        background: "var(--surface-alt)",
        listStyle: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, opacity: 0.5 }}>
            {expanded ? "▼" : "▶"}
          </span>
          <strong style={{ fontSize: 14 }}>
            Facebook Account -{" "}
            {account.kernelProfileName || account.id.slice(0, 8)}
          </strong>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              color: isLoginRequired ? "var(--error)" : "var(--primary)",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            {isLoginRequired ? "Login Required" : "Connected"}
          </span>
        </div>
      </div>

      {expanded && (
        <div
          style={{
            marginTop: 12,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                ID: <code>{account.id}</code>
              </div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                Profile: {account.kernelProfileName}
              </div>
              {account.secretToken && (
                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                  Webhook secret: <code>{account.secretToken}</code>
                </div>
              )}
              {account.lastEventTime && (
                <div
                  style={{
                    fontSize: 12,
                    opacity: 0.8,
                    color: "var(--primary)",
                    marginTop: 4,
                  }}
                >
                  Last event: {new Date(account.lastEventTime).toLocaleString()}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <Button
                className="primary"
                style={{ fontSize: 11, padding: "2px 8px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  openFbLogin(account.id);
                }}
              >
                Login
              </Button>
              {activeLoginSessions[account.id] && (
                <Button
                  style={{ fontSize: 11, padding: "2px 8px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    finishFbLogin(account.id);
                  }}
                >
                  Done logging in
                </Button>
              )}
              <Button
                style={{ fontSize: 11, padding: "2px 8px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  releaseFbAccount(account.id);
                }}
              >
                Release
              </Button>
            </div>
          </div>

          <hr
            style={{
              border: "0",
              borderTop: "1px solid var(--border)",
              margin: "4px 0",
            }}
          />

          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              Webhook URLs (one per line):
            </div>
            <textarea
              style={{
                width: "100%",
                minHeight: 60,
                fontSize: 12,
                padding: 8,
                borderRadius: 4,
                border: "1px solid var(--border)",
              }}
              value={webhookUrlsText}
              onChange={(e) => setWebhookUrlsText(e.currentTarget.value)}
            />
            <Button
              className="primary"
              style={{
                fontSize: 11,
                padding: "4px 10px",
                marginTop: 4,
              }}
              onClick={() => saveWebhooks(account.id, webhookUrlsText)}
            >
              Save webhooks
            </Button>
          </div>

          <hr
            style={{
              border: "0",
              borderTop: "1px solid var(--border)",
              margin: "4px 0",
            }}
          />

          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              Watched groups ({account.fbGroups?.length || 0}):
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(account.fbGroups || []).map((group) => (
                <div
                  key={group.id}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    background: "var(--surface)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 600 }}>
                      {group.name || group.groupId}
                    </span>
                    <Button
                      style={{ fontSize: 10, padding: "2px 6px" }}
                      onClick={() => removeGroup(group.id)}
                    >
                      Remove
                    </Button>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      marginTop: 4,
                      fontSize: 11,
                      opacity: 0.6,
                    }}
                  >
                    <span>
                      Last scraped: {group.lastScrapedAt
                        ? new Date(group.lastScrapedAt).toLocaleString()
                        : "Never (pending)"}
                    </span>
                    {group.lastEventTime && (
                      <span>
                        • Last event:{" "}
                        {new Date(group.lastEventTime).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                marginTop: 12,
                flexWrap: "wrap",
              }}
            >
              <input
                placeholder="Group ID"
                style={{
                  flex: 1,
                  minWidth: 120,
                  fontSize: 12,
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: "1px solid var(--border)",
                }}
                value={newGroupId}
                onChange={(e) => setNewGroupId(e.currentTarget.value)}
              />
              <input
                placeholder="Name (optional)"
                style={{
                  flex: 1,
                  minWidth: 120,
                  fontSize: 12,
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: "1px solid var(--border)",
                }}
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.currentTarget.value)}
              />
              <Button
                className="primary"
                style={{ fontSize: 11, padding: "4px 10px" }}
                onClick={handleAddGroup}
              >
                Add group
              </Button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
};

const FacebookAccounts = () => {
  const { user: authUser } = useAuth();
  const [activeLoginSessions, setActiveLoginSessions] = useState<
    Record<string, string>
  >({});

  const { isLoading, error, data } = useQuery(
    authUser?.email
      ? {
        fbAccounts: {
          fbGroups: {},
          $: {
            where: {
              "admins.email": authUser.email,
              status: { $in: ["active", "loginRequired"] },
            },
          },
        },
      }
      : null,
  );

  if (!authUser || isLoading) return null;
  if (error) return <div>Error loading FB accounts</div>;

  const refreshToken = authUser.refresh_token;
  const fbAccounts = data?.fbAccounts || [];

  const addFbAccount_ = async () => {
    if (!refreshToken) return notAuthenticated();
    const promise = userClient({
      endpoint: "createFbAccount",
      payload: { refreshToken },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    await toast.promise(promise, {
      loading: "Adding FB account...",
      success: "FB account added",
      error: (e) => e?.message || "Failed to add FB account.",
    });
  };

  const releaseFbAccount_ = async (fbAccountId: string) => {
    if (!refreshToken) return notAuthenticated();
    if (
      !await confirmDialog("Release this FB account?", {
        title: "Release FB account",
        okLabel: "Release",
        danger: true,
      })
    ) {
      return;
    }
    const promise = userClient({
      endpoint: "releaseFbAccount",
      payload: { refreshToken, fbAccountId },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    await toast.promise(promise, {
      loading: "Releasing FB account...",
      success: "FB account released",
      error: (e) => e?.message || "Failed to release FB account",
    });
  };

  const addGroup = async (
    fbAccountId: string,
    groupId: string,
    name?: string,
  ) => {
    if (!refreshToken) return notAuthenticated();
    const promise = userClient({
      endpoint: "addFbGroup",
      payload: {
        refreshToken,
        fbAccountId,
        groupId,
        name,
      },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    await toast.promise(promise, {
      loading: "Adding group...",
      success: "Group added",
      error: (e) => e?.message || "Failed to add group",
    });
  };

  const removeGroup = async (fbGroupId: string) => {
    if (!refreshToken) return notAuthenticated();
    const promise = userClient({
      endpoint: "removeFbGroup",
      payload: { refreshToken, fbGroupId },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    await toast.promise(promise, {
      loading: "Removing group...",
      success: "Group removed",
      error: (e) => e?.message || "Failed to remove group",
    });
  };

  const saveWebhooks = async (fbAccountId: string, urlsText: string) => {
    if (!refreshToken) return notAuthenticated();
    const urls = urlsText.split("\n").map((u) => u.trim()).filter(Boolean);
    const promise = userClient({
      endpoint: "setFbAccountWebhooks",
      payload: { refreshToken, fbAccountId, webhookUrls: urls },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    await toast.promise(promise, {
      loading: "Saving webhooks...",
      success: "Webhooks saved",
      error: (e) => e?.message || "Failed to save webhooks",
    });
  };

  const openFbLogin = async (fbAccountId: string) => {
    if (!refreshToken) return notAuthenticated();
    const promise = userClient({
      endpoint: "getFbAccountLoginUrl",
      payload: { refreshToken, fbAccountId },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    const result = await toast.promise(promise, {
      loading: "Opening browser...",
      success: "Browser opened",
      error: (e) => e?.message || "Failed to open browser",
    });
    setActiveLoginSessions((prev) => ({
      ...prev,
      [fbAccountId]: result.sessionId,
    }));
    globalThis.open(result.loginUrl, "_blank");
  };

  const finishFbLogin = async (fbAccountId: string) => {
    if (!refreshToken) return notAuthenticated();
    const sessionId = activeLoginSessions[fbAccountId];
    if (!sessionId) return;
    await toast.promise(
      userClient({
        endpoint: "closeFbAccountLoginSession",
        payload: { refreshToken, sessionId },
      }).then((res) => {
        if ("error" in res) throw new Error(String(res.error));
        return res;
      }),
      {
        loading: "Saving session...",
        success: "Login saved",
        error: (e) => e?.message || "Failed to save session",
      },
    );
    setActiveLoginSessions((prev) => {
      const { [fbAccountId]: _, ...rest } = prev;
      return rest;
    });
  };

  return (
    <div style={fbAccountContainerStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h2 style={{ margin: 0 }}>Facebook accounts</h2>
        <Button
          className="primary"
          style={{ fontSize: 12, padding: "4px 12px" }}
          onClick={addFbAccount_}
        >
          + Add FB account
        </Button>
      </div>
      {fbAccounts.length === 0 ? <div>No Facebook accounts yet.</div> : (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {fbAccounts.map((account) => (
            <FbAccountCard
              key={account.id}
              account={account}
              activeLoginSessions={activeLoginSessions}
              openFbLogin={openFbLogin}
              finishFbLogin={finishFbLogin}
              releaseFbAccount={releaseFbAccount_}
              addGroup={addGroup}
              removeGroup={removeGroup}
              saveWebhooks={saveWebhooks}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

const PhoneNumbers = () => {
  const { user: authUser } = useAuth();
  const { isLoading, error, data } = useQuery(
    authUser?.email
      ? {
        phoneNumbers: {
          smsMessages: {},
          $: {
            where: {
              "admins.email": authUser.email,
              status: "active",
            },
          },
        },
      }
      : null,
  );
  if (!authUser || isLoading) return null;
  if (error) return <div>Error loading numbers</div>;
  const refreshToken = authUser.refresh_token;
  return (
    <PhoneNumbersView
      phoneNumbers={data?.phoneNumbers || []}
      onAdd={refreshToken ? addPbxmeNumber(refreshToken) : notAuthenticated}
      onRelease={refreshToken
        ? (n) => releasePhoneNumber(refreshToken, n)()
        : notAuthenticated}
    />
  );
};

const adminPanelContainerStyle = {
  padding: 20,
  display: "flex",
  flexDirection: "column" as const,
  gap: 20,
};

const adminStatsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
};

const adminStatCardStyle = {
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  padding: 16,
  background: "var(--surface-alt)",
  display: "flex",
  flexDirection: "column" as const,
  gap: 6,
  textAlign: "center" as const,
};

const adminStatValueStyle = {
  fontSize: 28,
  fontWeight: 800,
  color: "var(--primary)",
};

const adminStatLabelStyle = {
  fontSize: 13,
  opacity: 0.8,
  fontWeight: 600,
};

const adminTableStyle = {
  width: "100%",
  borderCollapse: "collapse" as const,
  fontSize: 13,
  marginTop: 10,
};

const adminTableHeaderStyle = {
  background: "var(--surface-alt)",
  borderBottom: "2px solid var(--border)",
  textAlign: "left" as const,
  padding: 10,
  fontWeight: 600,
};

const adminTableCellStyle = {
  borderBottom: "1px solid var(--border)",
  padding: 10,
  verticalAlign: "middle" as const,
};

const adminSubTabBarStyle = {
  display: "flex",
  gap: 8,
  borderBottom: "1px solid var(--border)",
  paddingBottom: 8,
  marginBottom: 10,
  flexWrap: "wrap" as const,
};

const adminSubTabButtonStyle = (active: boolean) => ({
  padding: "6px 12px",
  borderRadius: "var(--radius)",
  border: "1px solid var(--border)",
  background: active ? "var(--primary)" : "var(--surface-alt)",
  color: active ? "white" : "var(--text)",
  fontSize: 13,
  fontWeight: active ? 600 : 400,
  cursor: "pointer",
});

interface AdminStats {
  numbersCount: number;
  connectedWhatsappCount: number;
  totalWhatsappCount: number;
  connectedTelegramCount: number;
  totalTelegramCount: number;
  totalUsersCount: number;
}

interface AdminWhatsappAccount {
  phoneNumber: string;
  name: string;
  isConnected: boolean;
  isEnabled: boolean;
  admins: string[];
  billingUser: string | null;
}

interface AdminTelegramAccount {
  phoneNumber: string;
  name: string;
  isConnected: boolean;
  isEnabled: boolean;
  admins: string[];
  billingUser: string | null;
}

interface AdminPhoneNumber {
  number: string;
  status: string | null;
  createdAt: number;
  admins: string[];
  billingUser: string | null;
}

interface AdminFbAccount {
  id: string;
  status: string | null;
  admins: string[];
  billingUser: string | null;
}

interface AdminUser {
  email: string;
  lastSeen: number;
  createdAt: number;
  monthlyPayment: number | null;
  paidCapacity: number | null;
  stripeCustomerId: string | null;
}

interface AdminStatsData {
  success: true;
  stats: AdminStats;
  whatsappAccounts: AdminWhatsappAccount[];
  telegramAccounts: AdminTelegramAccount[];
  phoneNumbers: AdminPhoneNumber[];
  fbAccounts: AdminFbAccount[];
  users: AdminUser[];
}

export const AdminPanel = () => {
  const { user: authUser } = useAuth();
  const [data, setData] = useState<AdminStatsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [subTab, setSubTab] = useState<
    "overview" | "whatsapp" | "telegram" | "phone" | "facebook" | "users"
  >("overview");
  const [userSortField, setUserSortField] = useState<
    keyof AdminUser | "billingStatus" | null
  >(null);
  const [userSortOrder, setUserSortOrder] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    if (!authUser?.refresh_token) {
      setError("Not authenticated");
      setIsLoading(false);
      return;
    }

    userClient({
      endpoint: "getAdminStats",
      payload: { refreshToken: authUser.refresh_token },
    })
      .then((res) => {
        if ("error" in res) {
          setError(String(res.error));
        } else if ("stats" in res) {
          setData(res);
        }
      })
      .catch((e: Error) => {
        setError(e?.message || "Failed to load admin stats");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [authUser?.refresh_token]);

  if (isLoading) {
    return <LoadingIndication text="Loading admin statistics..." />;
  }

  if (error) {
    return (
      <div style={{ padding: 20 }}>
        <ErrorMessage error={{ message: error }} />
      </div>
    );
  }

  if (!data || !data.success) {
    return (
      <div style={{ padding: 20 }}>
        <ErrorMessage error={{ message: "Failed to retrieve data" }} />
      </div>
    );
  }

  const {
    stats,
    whatsappAccounts,
    telegramAccounts,
    phoneNumbers,
    fbAccounts,
    users,
  } = data;

  const filterFn = <T,>(
    item: T,
    fields: Array<keyof T & string>,
  ) => {
    if (!searchTerm) return true;
    const lower = searchTerm.toLowerCase();
    return fields.some((f) => {
      const val = item[f];
      if (!val) return false;
      if (Array.isArray(val)) {
        return val.some((v) => String(v).toLowerCase().includes(lower));
      }
      return String(val).toLowerCase().includes(lower);
    });
  };

  const filteredWhatsapp = whatsappAccounts.filter((a) =>
    filterFn(a, ["phoneNumber", "name", "admins", "billingUser"])
  );
  const filteredTelegram = telegramAccounts.filter((a) =>
    filterFn(a, ["phoneNumber", "name", "admins", "billingUser"])
  );
  const filteredPhoneNumbers = phoneNumbers.filter((p) =>
    filterFn(p, ["number", "status", "admins", "billingUser"])
  );
  const filteredFbAccounts = fbAccounts.filter((f) =>
    filterFn(f, ["id", "status", "admins", "billingUser"])
  );
  const filteredUsers = users.filter((u) =>
    filterFn(u, ["email", "stripeCustomerId"])
  );

  const sortedUsers = useMemo(() => {
    if (!userSortField) return filteredUsers;
    return [...filteredUsers].sort((a, b) => {
      const getVal = (u: AdminUser) => {
        if (userSortField === "billingStatus") {
          return (u.monthlyPayment || 0) > 0 || (u.paidCapacity || 0) > 0 ||
              !!u.stripeCustomerId
            ? 1
            : 0;
        }
        return u[userSortField];
      };
      const valA = getVal(a);
      const valB = getVal(b);

      if (valA === null || valA === undefined) {
        if (valB === null || valB === undefined) return 0;
        return userSortOrder === "asc" ? 1 : -1;
      }
      if (valB === null || valB === undefined) {
        return userSortOrder === "asc" ? -1 : 1;
      }

      if (typeof valA === "string" && typeof valB === "string") {
        return userSortOrder === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      const numA = Number(valA);
      const numB = Number(valB);
      return userSortOrder === "asc" ? numA - numB : numB - numA;
    });
  }, [filteredUsers, userSortField, userSortOrder]);

  const handleSort = (field: keyof AdminUser | "billingStatus") => {
    if (userSortField === field) {
      setUserSortOrder(userSortOrder === "asc" ? "desc" : "asc");
    } else {
      setUserSortField(field);
      setUserSortOrder("asc");
    }
  };

  const renderSortIndicator = (field: keyof AdminUser | "billingStatus") => {
    if (userSortField !== field) return null;
    return (
      <span style={{ fontSize: 10, marginLeft: 4, display: "inline-block" }}>
        {userSortOrder === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  const getHeaderStyle = (field: keyof AdminUser | "billingStatus") => ({
    ...adminTableHeaderStyle,
    cursor: "pointer",
    userSelect: "none",
    background: userSortField === field
      ? "var(--surface)"
      : "var(--surface-alt)",
  });

  return (
    <div style={adminPanelContainerStyle}>
      <h2 style={{ margin: 0 }}>Supergreen Admin Panel</h2>

      {/* Stats Cards */}
      <div style={adminStatsGridStyle}>
        <div style={adminStatCardStyle}>
          <span style={adminStatValueStyle}>{stats.totalUsersCount}</span>
          <span style={adminStatLabelStyle}>Total Users</span>
        </div>
        <div style={adminStatCardStyle}>
          <span style={adminStatValueStyle}>
            {stats.connectedWhatsappCount} / {stats.totalWhatsappCount}
          </span>
          <span style={adminStatLabelStyle}>WhatsApp Connected</span>
        </div>
        <div style={adminStatCardStyle}>
          <span style={adminStatValueStyle}>
            {stats.connectedTelegramCount} / {stats.totalTelegramCount}
          </span>
          <span style={adminStatLabelStyle}>Telegram Connected</span>
        </div>
        <div style={adminStatCardStyle}>
          <span style={adminStatValueStyle}>{stats.numbersCount}</span>
          <span style={adminStatLabelStyle}>Purchased Numbers</span>
        </div>
      </div>

      {/* Sub Tabs Navigation */}
      <div style={adminSubTabBarStyle}>
        <button
          type="button"
          style={adminSubTabButtonStyle(subTab === "overview")}
          onClick={() => setSubTab("overview")}
        >
          Overview
        </button>
        <button
          type="button"
          style={adminSubTabButtonStyle(subTab === "users")}
          onClick={() => setSubTab("users")}
        >
          Users ({filteredUsers.length})
        </button>
        <button
          type="button"
          style={adminSubTabButtonStyle(subTab === "whatsapp")}
          onClick={() => setSubTab("whatsapp")}
        >
          WhatsApp ({filteredWhatsapp.length})
        </button>
        <button
          type="button"
          style={adminSubTabButtonStyle(subTab === "telegram")}
          onClick={() => setSubTab("telegram")}
        >
          Telegram ({filteredTelegram.length})
        </button>
        <button
          type="button"
          style={adminSubTabButtonStyle(subTab === "phone")}
          onClick={() => setSubTab("phone")}
        >
          Phone Numbers ({filteredPhoneNumbers.length})
        </button>
        <button
          type="button"
          style={adminSubTabButtonStyle(subTab === "facebook")}
          onClick={() => setSubTab("facebook")}
        >
          Facebook ({filteredFbAccounts.length})
        </button>
      </div>

      {/* Master Search Bar */}
      <div style={{ display: "flex", gap: 10 }}>
        <input
          type="text"
          placeholder="Search by phone, email, name, or Stripe customer ID..."
          style={inputStyle}
          value={searchTerm}
          onInput={(e) => setSearchTerm(e.currentTarget.value)}
        />
        {searchTerm && (
          <Button
            className="secondary"
            onClick={() => setSearchTerm("")}
          >
            Clear
          </Button>
        )}
      </div>

      <div style={{ overflowX: "auto" }}>
        {subTab === "overview" && (
          <div
            style={{
              padding: 16,
              background: "var(--surface-alt)",
              borderRadius: "var(--radius)",
              border: "1px solid var(--border)",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <h3 style={{ margin: 0, fontSize: 16 }}>Admin Overview Guide</h3>
            <p
              style={{
                margin: 0,
                opacity: 0.85,
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              Welcome back, Uri! This panel aggregates live statistics on all
              connected numbers, users, associated accounts, and subscription
              statuses directly from the database.
            </p>
            <ul
              style={{
                margin: 0,
                paddingLeft: 18,
                fontSize: 13,
                display: "flex",
                flexDirection: "column",
                gap: 6,
                opacity: 0.85,
              }}
            >
              <li>
                <strong>Users Tab</strong>{" "}
                shows everyone signed up on the platform, when they were last
                seen, their monthly payment amount, paid capacities, and whether
                they are paying or on free trial.
              </li>
              <li>
                <strong>WhatsApp / Telegram / Facebook Tabs</strong>{" "}
                list every individual communication bot registered in the
                system, their real-time connection status, who admins them, and
                the user billed for their server usage.
              </li>
              <li>
                <strong>Phone Numbers Tab</strong>{" "}
                details all Israeli DIDs purchased through PBXME and which user
                currently holds them.
              </li>
            </ul>
          </div>
        )}

        {subTab === "users" && (
          <table style={adminTableStyle}>
            <thead>
              <tr>
                <th
                  style={getHeaderStyle("email")}
                  onClick={() => handleSort("email")}
                >
                  User Email{renderSortIndicator("email")}
                </th>
                <th
                  style={getHeaderStyle("lastSeen")}
                  onClick={() => handleSort("lastSeen")}
                >
                  Last Seen{renderSortIndicator("lastSeen")}
                </th>
                <th
                  style={getHeaderStyle("createdAt")}
                  onClick={() => handleSort("createdAt")}
                >
                  Created At{renderSortIndicator("createdAt")}
                </th>
                <th
                  style={getHeaderStyle("monthlyPayment")}
                  onClick={() => handleSort("monthlyPayment")}
                >
                  Monthly Payment{renderSortIndicator("monthlyPayment")}
                </th>
                <th
                  style={getHeaderStyle("paidCapacity")}
                  onClick={() => handleSort("paidCapacity")}
                >
                  Paid Capacity{renderSortIndicator("paidCapacity")}
                </th>
                <th
                  style={getHeaderStyle("stripeCustomerId")}
                  onClick={() => handleSort("stripeCustomerId")}
                >
                  Stripe Customer{renderSortIndicator("stripeCustomerId")}
                </th>
                <th
                  style={getHeaderStyle("billingStatus")}
                  onClick={() => handleSort("billingStatus")}
                >
                  Billing Status{renderSortIndicator("billingStatus")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        ...adminTableCellStyle,
                        textAlign: "center",
                        opacity: 0.6,
                      }}
                    >
                      No users match your criteria.
                    </td>
                  </tr>
                )
                : (
                  sortedUsers.map((u) => {
                    const isPaying = (u.monthlyPayment || 0) > 0 ||
                      (u.paidCapacity || 0) > 0 || !!u.stripeCustomerId;
                    return (
                      <tr key={u.email}>
                        <td style={{ ...adminTableCellStyle, fontWeight: 600 }}>
                          {u.email}
                        </td>
                        <td style={adminTableCellStyle}>
                          {u.lastSeen
                            ? new Date(u.lastSeen).toLocaleString()
                            : "Never"}
                        </td>
                        <td style={adminTableCellStyle}>
                          {u.createdAt
                            ? new Date(u.createdAt).toLocaleString()
                            : "Unknown"}
                        </td>
                        <td style={adminTableCellStyle}>
                          {u.monthlyPayment !== null
                            ? `$${u.monthlyPayment}/mo`
                            : "$0"}
                        </td>
                        <td style={adminTableCellStyle}>
                          {u.paidCapacity !== null
                            ? `${u.paidCapacity} slots`
                            : "0"}
                        </td>
                        <td style={adminTableCellStyle}>
                          {u.stripeCustomerId
                            ? (
                              <code style={{ fontSize: 11 }}>
                                {u.stripeCustomerId}
                              </code>
                            )
                            : <span style={{ opacity: 0.5 }}>None</span>}
                        </td>
                        <td style={adminTableCellStyle}>
                          <span
                            style={{
                              background: isPaying
                                ? "rgba(46, 125, 50, 0.15)"
                                : "rgba(198, 40, 40, 0.1)",
                              color: isPaying ? "#2e7d32" : "#c62828",
                              padding: "3px 8px",
                              borderRadius: 4,
                              fontWeight: 600,
                              fontSize: 11,
                            }}
                          >
                            {isPaying ? "Paying User" : "Free / Trial"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
            </tbody>
          </table>
        )}

        {subTab === "whatsapp" && (
          <table style={adminTableStyle}>
            <thead>
              <tr>
                <th style={adminTableHeaderStyle}>Phone Number</th>
                <th style={adminTableHeaderStyle}>Bot Name</th>
                <th style={adminTableHeaderStyle}>Admins</th>
                <th style={adminTableHeaderStyle}>Billing User</th>
                <th style={adminTableHeaderStyle}>Connection</th>
                <th style={adminTableHeaderStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredWhatsapp.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        ...adminTableCellStyle,
                        textAlign: "center",
                        opacity: 0.6,
                      }}
                    >
                      No WhatsApp accounts found.
                    </td>
                  </tr>
                )
                : (
                  filteredWhatsapp.map((a) => (
                    <tr key={a.phoneNumber}>
                      <td style={{ ...adminTableCellStyle, fontWeight: 600 }}>
                        {a.phoneNumber}
                      </td>
                      <td style={adminTableCellStyle}>
                        {a.name || (
                          <span style={{ opacity: 0.5 }}>No Name</span>
                        )}
                      </td>
                      <td style={adminTableCellStyle}>
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                        >
                          {a.admins.map((email: string) => (
                            <span
                              key={email}
                              style={{
                                background: "var(--surface)",
                                border: "1px solid var(--border)",
                                borderRadius: 4,
                                padding: "2px 6px",
                                fontSize: 11,
                              }}
                            >
                              {email}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={adminTableCellStyle}>
                        {a.billingUser || (
                          <span style={{ opacity: 0.5 }}>Unassigned</span>
                        )}
                      </td>
                      <td style={adminTableCellStyle}>
                        <span
                          style={{
                            color: a.isConnected ? "#2e7d32" : "#c62828",
                            fontWeight: 600,
                          }}
                        >
                          {a.isConnected ? "🟢 Connected" : "🔴 Disconnected"}
                        </span>
                      </td>
                      <td style={adminTableCellStyle}>
                        <span
                          style={{
                            background: a.isEnabled
                              ? "rgba(46, 125, 50, 0.15)"
                              : "rgba(245, 124, 0, 0.15)",
                            color: a.isEnabled ? "#2e7d32" : "#f57c00",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {a.isEnabled ? "Active" : "Suspended"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
            </tbody>
          </table>
        )}

        {subTab === "telegram" && (
          <table style={adminTableStyle}>
            <thead>
              <tr>
                <th style={adminTableHeaderStyle}>Phone Number</th>
                <th style={adminTableHeaderStyle}>Bot Name</th>
                <th style={adminTableHeaderStyle}>Admins</th>
                <th style={adminTableHeaderStyle}>Billing User</th>
                <th style={adminTableHeaderStyle}>Connection</th>
                <th style={adminTableHeaderStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredTelegram.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        ...adminTableCellStyle,
                        textAlign: "center",
                        opacity: 0.6,
                      }}
                    >
                      No Telegram accounts found.
                    </td>
                  </tr>
                )
                : (
                  filteredTelegram.map((a) => (
                    <tr key={a.phoneNumber}>
                      <td style={{ ...adminTableCellStyle, fontWeight: 600 }}>
                        {a.phoneNumber}
                      </td>
                      <td style={adminTableCellStyle}>
                        {a.name || (
                          <span style={{ opacity: 0.5 }}>No Name</span>
                        )}
                      </td>
                      <td style={adminTableCellStyle}>
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                        >
                          {a.admins.map((email: string) => (
                            <span
                              key={email}
                              style={{
                                background: "var(--surface)",
                                border: "1px solid var(--border)",
                                borderRadius: 4,
                                padding: "2px 6px",
                                fontSize: 11,
                              }}
                            >
                              {email}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={adminTableCellStyle}>
                        {a.billingUser || (
                          <span style={{ opacity: 0.5 }}>Unassigned</span>
                        )}
                      </td>
                      <td style={adminTableCellStyle}>
                        <span
                          style={{
                            color: a.isConnected ? "#2e7d32" : "#c62828",
                            fontWeight: 600,
                          }}
                        >
                          {a.isConnected ? "🟢 Connected" : "🔴 Disconnected"}
                        </span>
                      </td>
                      <td style={adminTableCellStyle}>
                        <span
                          style={{
                            background: a.isEnabled
                              ? "rgba(46, 125, 50, 0.15)"
                              : "rgba(245, 124, 0, 0.15)",
                            color: a.isEnabled ? "#2e7d32" : "#f57c00",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {a.isEnabled ? "Active" : "Suspended"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
            </tbody>
          </table>
        )}

        {subTab === "phone" && (
          <table style={adminTableStyle}>
            <thead>
              <tr>
                <th style={adminTableHeaderStyle}>Number</th>
                <th style={adminTableHeaderStyle}>Status</th>
                <th style={adminTableHeaderStyle}>Created At</th>
                <th style={adminTableHeaderStyle}>Admins</th>
                <th style={adminTableHeaderStyle}>Billing User</th>
              </tr>
            </thead>
            <tbody>
              {filteredPhoneNumbers.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        ...adminTableCellStyle,
                        textAlign: "center",
                        opacity: 0.6,
                      }}
                    >
                      No phone numbers found.
                    </td>
                  </tr>
                )
                : (
                  filteredPhoneNumbers.map((p) => (
                    <tr key={p.number}>
                      <td style={{ ...adminTableCellStyle, fontWeight: 600 }}>
                        {p.number}
                      </td>
                      <td style={adminTableCellStyle}>
                        <span
                          style={{
                            background: p.status === "active"
                              ? "rgba(46, 125, 50, 0.15)"
                              : "rgba(198, 40, 40, 0.1)",
                            color: p.status === "active"
                              ? "#2e7d32"
                              : "#c62828",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {p.status || "Unknown"}
                        </span>
                      </td>
                      <td style={adminTableCellStyle}>
                        {p.createdAt
                          ? new Date(p.createdAt).toLocaleString()
                          : "Unknown"}
                      </td>
                      <td style={adminTableCellStyle}>
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                        >
                          {p.admins.map((email: string) => (
                            <span
                              key={email}
                              style={{
                                background: "var(--surface)",
                                border: "1px solid var(--border)",
                                borderRadius: 4,
                                padding: "2px 6px",
                                fontSize: 11,
                              }}
                            >
                              {email}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={adminTableCellStyle}>
                        {p.billingUser || (
                          <span style={{ opacity: 0.5 }}>Unassigned</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
            </tbody>
          </table>
        )}

        {subTab === "facebook" && (
          <table style={adminTableStyle}>
            <thead>
              <tr>
                <th style={adminTableHeaderStyle}>Account ID</th>
                <th style={adminTableHeaderStyle}>Status</th>
                <th style={adminTableHeaderStyle}>Admins</th>
                <th style={adminTableHeaderStyle}>Billing User</th>
              </tr>
            </thead>
            <tbody>
              {filteredFbAccounts.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        ...adminTableCellStyle,
                        textAlign: "center",
                        opacity: 0.6,
                      }}
                    >
                      No Facebook accounts found.
                    </td>
                  </tr>
                )
                : (
                  filteredFbAccounts.map((f) => (
                    <tr key={f.id}>
                      <td
                        style={{
                          ...adminTableCellStyle,
                          fontWeight: 600,
                          fontFamily: "monospace",
                        }}
                      >
                        {f.id}
                      </td>
                      <td style={adminTableCellStyle}>
                        <span
                          style={{
                            background: f.status === "active"
                              ? "rgba(46, 125, 50, 0.15)"
                              : "rgba(198, 40, 40, 0.1)",
                            color: f.status === "active"
                              ? "#2e7d32"
                              : "#c62828",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {f.status || "Unknown"}
                        </span>
                      </td>
                      <td style={adminTableCellStyle}>
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                        >
                          {f.admins.map((email: string) => (
                            <span
                              key={email}
                              style={{
                                background: "var(--surface)",
                                border: "1px solid var(--border)",
                                borderRadius: 4,
                                padding: "2px 6px",
                                fontSize: 11,
                              }}
                            >
                              {email}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={adminTableCellStyle}>
                        {f.billingUser || (
                          <span style={{ opacity: 0.5 }}>Unassigned</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

type TabKey = DashboardTab;

const tabOrder: { key: TabKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "telegram", label: "Telegram" },
  { key: "facebook", label: "Facebook" },
  { key: "phone", label: "Phone Numbers" },
  { key: "billing", label: "Billing" },
];

const tabBarStyle = {
  display: "flex",
  gap: 4,
  padding: "12px 20px 0",
  borderBottom: "1px solid var(--border)",
  background: "var(--surface)",
  overflowX: "auto" as const,
  overflowY: "hidden" as const,
};

const tabButtonStyle = (active: boolean) => ({
  padding: "8px 16px",
  borderRadius: "var(--radius) var(--radius) 0 0",
  border: "1px solid var(--border)",
  borderBottom: active ? "1px solid var(--surface)" : "1px solid var(--border)",
  background: active ? "var(--surface)" : "var(--surface-alt)",
  color: active ? "var(--text)" : "var(--text-muted)",
  fontWeight: active ? 600 : 400,
  fontSize: 14,
  cursor: "pointer",
  whiteSpace: "nowrap" as const,
  marginBottom: active ? -1 : 0,
});

const tabKeys = new Set<string>(tabOrder.map(({ key }) => key));

const isTabKey = (value: string): value is TabKey => tabKeys.has(value);

type DeepLink = { tab: TabKey; account?: string };

// Deep link format: `#<tab>` or `#<tab>/<accountId>` (e.g. #whatsapp/972501234567)
const parseHash = (hash: string): DeepLink | null => {
  const [tab, account] = hash.replace(/^#/, "").split("/");
  if (!tab || !isTabKey(tab)) return null;
  return account ? { tab, account: decodeURIComponent(account) } : { tab };
};

const deepLinkHash = ({ tab, account }: DeepLink) =>
  account ? `#${tab}/${encodeURIComponent(account)}` : `#${tab}`;

const readDeepLink = (): DeepLink | null =>
  typeof globalThis.location === "undefined"
    ? null
    : parseHash(globalThis.location.hash);

const useDeepLink = (): [DeepLink, (link: DeepLink) => void] => {
  const [deepLink, setDeepLink] = useState<DeepLink>(
    () => readDeepLink() ?? { tab: "overview" },
  );
  useEffect(() => {
    const onHashChange = () => {
      const parsed = readDeepLink();
      if (parsed) setDeepLink(parsed);
    };
    globalThis.addEventListener("hashchange", onHashChange);
    return () => globalThis.removeEventListener("hashchange", onHashChange);
  }, []);
  const navigate = (link: DeepLink) => {
    setDeepLink(link);
    const url = new URL(globalThis.location.href);
    url.hash = deepLinkHash(link);
    globalThis.history.replaceState({}, "", url.toString());
  };
  return [deepLink, navigate];
};

const DashboardTabs = () => {
  const [{ tab: activeTab }, navigate] = useDeepLink();
  const setActiveTab = (tab: TabKey) => navigate({ tab });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
      }}
    >
      <div style={tabBarStyle}>
        {tabOrder.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            style={tabButtonStyle(activeTab === key)}
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>
      <div style={{ overflowY: "auto", flex: 1 }}>
        {activeTab === "overview" && (
          <>
            <UserProfile />
            <div style={{ padding: "0 20px" }}>
              <BillingSummary />
            </div>
          </>
        )}
        {activeTab === "whatsapp" && <WhatsappAccounts />}
        {activeTab === "telegram" && <TelegramAccounts />}
        {activeTab === "facebook" && <FacebookAccounts />}
        {activeTab === "phone" && <PhoneNumbers />}
        {activeTab === "billing" && <BillingTab />}
      </div>
    </div>
  );
};

const BillingSummary = () => {
  const data = useBillingData();
  if (!data) return null;
  const {
    waCount,
    tgCount,
    numbers,
    fbCount,
    spent,
    remaining,
    budget,
    capacity,
    trialCapacity,
    isTrial,
    trialEnd,
  } = data;

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: "var(--radius)",
        padding: 16,
        background: "var(--surface-alt)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        marginTop: 12,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600 }}>Billing Summary</div>
      <div style={{ fontSize: 13, opacity: 0.9 }}>
        <div>
          Budget: <strong>${budget}/mo</strong> ({capacity}{" "}
          slot{capacity === 1 ? "" : "s"})
          {" — "}
          Spent: <strong>${spent}</strong>, Remaining:{" "}
          <strong>${remaining}</strong>
        </div>
        {trialCapacity > 0 && (
          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
            Trial includes {trialCapacity}{" "}
            WhatsApp, Telegram, or Facebook slot. Phone numbers require paid
            budget.
          </div>
        )}
        {spent > 0 && (
          <div
            style={{
              marginTop: 4,
              display: "flex",
              flexWrap: "wrap",
              gap: "4px 12px",
            }}
          >
            {waCount > 0 && <span>WhatsApp: {waCount}</span>}
            {tgCount > 0 && <span>Telegram: {tgCount}</span>}
            {numbers.length > 0 && <span>Phone Numbers: {numbers.length}</span>}
            {fbCount > 0 && <span>Facebook: {fbCount}</span>}
          </div>
        )}
        {capacity === 0 && (
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
            {isTrial
              ? <span>Trial expires on {trialEnd.toLocaleDateString()}</span>
              : <span>Free trial ended. Add budget to add services.</span>}
          </div>
        )}
      </div>
    </div>
  );
};

const SignedInHome = () => (
  <div style={wrapperStyle}>
    <Header />
    <DashboardTabs />
    <Footer />
  </div>
);

const BillingPage = () => (
  <div style={wrapperStyle}>
    <Header />
    <div style={{ overflowY: "auto", flex: 1 }}>
      <BillingTab />
    </div>
    <Footer />
  </div>
);

const DocsPage = () => (
  <div style={wrapperStyle}>
    <Header />
    <ApiDocs />
    <Footer />
  </div>
);

const pricingContainerStyle = {
  maxWidth: 900,
  margin: "0 auto",
  padding: "40px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 24,
  alignItems: "center",
  textAlign: "center",
} as const;

const pricingCardsWrapperStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 24,
  justifyContent: "center",
  width: "100%",
  marginTop: 16,
} as const;

const pricingCardStyle = {
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  padding: 32,
  background: "var(--surface-alt)",
  width: "100%",
  maxWidth: 380,
  flex: "1 1 300px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
  alignItems: "center",
} as const;

const PricingPage = () => (
  <div style={wrapperStyle}>
    <Header />
    <div style={pricingContainerStyle}>
      <h1 style={{ margin: 0, fontSize: "2.5em", fontWeight: 700 }}>Pricing</h1>
      <p
        style={{
          color: "var(--text-muted)",
          margin: 0,
          maxWidth: 600,
          fontSize: "1.1em",
        }}
      >
        Simple, modular pricing. Connect your communication slots and provision
        dedicated virtual numbers seamlessly.
      </p>

      <div style={pricingCardsWrapperStyle}>
        {/* Card 1: Service Slots */}
        <div style={pricingCardStyle}>
          <div
            style={{
              fontSize: "1.25em",
              fontWeight: 600,
              color: "var(--primary)",
            }}
          >
            Service Slots
          </div>
          <div style={{ fontSize: "3em", fontWeight: 700 }}>
            ${monthlyPaymentPerLine}
          </div>
          <div
            style={{
              color: "var(--text-muted)",
              fontSize: "1em",
              marginTop: -8,
            }}
          >
            per active slot / month
          </div>
          <p
            style={{
              fontSize: 14,
              color: "var(--text-muted)",
              margin: "8px 0",
            }}
          >
            A flexible slot that can run any WhatsApp account, Telegram account,
            or Facebook scraping session.
          </p>
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "16px 0",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              fontSize: 14,
              textAlign: "left",
              width: "100%",
            }}
          >
            <li>✓ Free trial to get started</li>
            <li>✓ WhatsApp (Web & API Integration)</li>
            <li>✓ Telegram (MTProto Client & Webhooks)</li>
            <li>✓ Facebook Group Scraping (Kernel browser)</li>
            <li>✓ Full REST API & raw payload Webhooks</li>
            <li>✓ Dedicated, isolated sandbox container</li>
          </ul>
        </div>

        {/* Card 2: Virtual Israeli Phone Numbers */}
        <div style={pricingCardStyle}>
          <div
            style={{
              fontSize: "1.25em",
              fontWeight: 600,
              color: "var(--primary)",
            }}
          >
            Israeli Virtual Numbers
          </div>
          <div
            style={{
              fontSize: "3em",
              fontWeight: 700,
              display: "flex",
              alignItems: "baseline",
              gap: 4,
            }}
          >
            Varies
          </div>
          <div
            style={{
              color: "var(--text-muted)",
              fontSize: "1em",
              marginTop: -8,
            }}
          >
            starting from ~${pbxmeNumberPriceUsd} / month
          </div>
          <p
            style={{
              fontSize: 14,
              color: "var(--text-muted)",
              margin: "8px 0",
            }}
          >
            Dedicated virtual Israeli phone numbers for real-time SMS receiving,
            OTP verification, and webhook forwarding.
          </p>
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "16px 0",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              fontSize: 14,
              textAlign: "left",
              width: "100%",
            }}
          >
            <li>✓ Clean, dedicated numbers (DIDs)</li>
            <li>✓ Setup fee & monthly cost vary by number</li>
            <li>✓ Real-time SMS routing to Webhooks</li>
            <li>✓ Perfect for WhatsApp/Telegram activation</li>
            <li>✓ Direct integration with slot services</li>
            <li>✓ Transparent pricing shown before purchase</li>
          </ul>
        </div>
      </div>

      <div
        style={{
          marginTop: 24,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          alignItems: "center",
        }}
      >
        <a
          href="/"
          class="button primary"
          style={{
            padding: "12px 40px",
            fontSize: 16,
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          Get Started
        </a>
        <p style={{ fontSize: 13, opacity: 0.8, margin: 0 }}>
          Have custom requirements or need help? Contact{" "}
          <a
            href={`mailto:${supportEmail}`}
            style={{ color: "var(--primary)", textDecoration: "underline" }}
          >
            {supportEmail}
          </a>
        </p>
      </div>
    </div>
    <Footer />
  </div>
);

const AdminPage = () => {
  const { user: authUser } = useAuth();
  if (authUser?.email !== "uri.valevski@gmail.com") {
    return <NotFound />;
  }
  return (
    <div style={wrapperStyle}>
      <Header />
      <div style={{ overflowY: "auto", flex: 1 }}>
        <AdminPanel />
      </div>
      <Footer />
    </div>
  );
};

const routes = [
  { path: "/app", component: SignedInHome },
  { path: "/billing", component: BillingPage },
  { path: "/admin", component: AdminPage },
];

const publicRoutes = [
  { path: "/", component: Login },
  { path: "/docs", component: DocsPage },
  { path: "/legal", component: LegalPage },
  { path: "/terms", component: TermsPage },
  { path: "/privacy", component: PrivacyPage },
  { path: "/refund-policy", component: RefundPolicyPage },
  { path: "/pricing", component: PricingPage },
];

const NotFound = () => <div>Page not found :(</div>;

const toastStyle = {
  background: "var(--surface-alt)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
} as const;

const UserProvider = ({ children }: { children: preact.ComponentChildren }) => {
  const { isLoading: authLoading, error: authError, user: authUser } =
    useAuth();

  const userQuery = useMemo(
    () =>
      authUser
        ? {
          users: { $: { where: { email: authUser.email ?? "" } } },
        }
        : null,
    [authUser?.email],
  );

  const { error: queryError, isLoading: queryLoading, data } = useQuery(
    userQuery,
  );

  const [created, setCreated] = useState(false);

  useEffect(() => {
    if (!authUser?.email) return;

    if (queryLoading) return;

    // If no user found and we haven't created one yet

    if ((!data?.users || data.users.length === 0) && !created) {
      console.log("Creating user for", authUser.email);

      setCreated(true); // Prevent double submission

      transact(
        tx.users[id()].update({
          lastSeen: Date.now(),

          pushSubscriptions: {},

          email: authUser.email,

          createdAt: Date.now(),
        }),
      );
    }
  }, [authUser, queryLoading, data, created]);

  const dbUser = data?.users?.[0] || null;

  useEffect(() => {
    if (dbUser) {
      transact(tx.users[dbUser.id].update({ lastSeen: Date.now() }));
    }
  }, [dbUser?.id]); // Only run when ID changes (stable)

  const isSSR = typeof window === "undefined";

  if (!isSSR && authError) return <ErrorMessage error={authError} />;

  if (!isSSR && authLoading) {
    return <LoadingIndication text="Loading auth..." />;
  }

  if (isSSR || !authUser) {
    return (
      <>
        {typeof window !== "undefined" &&
          <Toaster toastOptions={{ duration: 4000, style: toastStyle }} />}

        <ErrorBoundary>
          <Router>
            {publicRoutes.map(({ path, component }) => (
              <Route path={path} component={component} />
            ))}

            <Route default component={Login} />
          </Router>
        </ErrorBoundary>
      </>
    );
  }

  if (queryError) {
    return <ErrorMessage error={queryError} />;
  }

  if (queryLoading || !dbUser) {
    return <LoadingIndication text="Loading your user data..." />;
  }

  return (
    <UserContext.Provider
      value={{ dbUser, isLoading: queryLoading, error: queryError }}
    >
      {children}
    </UserContext.Provider>
  );
};

const App = () => (
  <UserProvider>
    {typeof window !== "undefined" &&
      <Toaster toastOptions={{ duration: 4000, style: toastStyle }} />}
    <DialogHost />
    <ErrorBoundary>
      <Router>
        {[...routes, ...publicRoutes].map(({ path, component }) => (
          <Route path={path} component={component} />
        ))}

        <Route default component={NotFound} />
      </Router>
    </ErrorBoundary>
  </UserProvider>
);

export const App_ = () => (
  <LocationProvider>
    <App />
  </LocationProvider>
);

hydrate(<App_ />);
